var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EMPTY_TEXT, LocaleCodes, MimeType, TagType, TileOrientation } from '@mitchell/react.component.ux';
import { CountryCode, EnterpriseDocStoreService, formatDateTime, getMimeType, MimeTypeService, TranslationService } from "../../common";
import { FileUploadStatus } from "../../file";
import { IntelligentAlbumHierarchy, IntelligentAssignmentSummary, IntelligentPhotoCaptureExcludedFileTypes, IntelligentPhotoCaptureDecodeConstants, PhotoCaptureAllowedMimeTypeConstant, PhotoCaptureFileExtensionAllowedForGallery } from '../constants';
import { processVinData } from './photoCaptureCommonUtilities';
import { applyVinLikeCrossTypeDemotion, getVinLikeFolderTypeCodeByOption, isVinLikeContentType } from './intelligentPhotoCaptureEstimatorVinOptionUtilities';
import { IntelligentPhotoCaptureArtifactsCategoryNames, IntelligentPhotoCaptureAssignmentSummaryStatus, IntelligentPhotoCaptureAttachmentIterationStatus, IntelligentPhotoCaptureEstimatorCategoryLabelName, IntelligentPhotoCaptureEstimatorCaptureMode, IntelligentPhotoCaptureEstimatorContentType, IntelligentPhotoCaptureEstimatorLabelTypeCode, IntelligentPhotoCaptureIQAQualityStatus, IntelligentPhotoCaptureRotationAngle, IntelligentPhotoCaptureVinOption, IntelligentPhotoCaptureVinDecodeStatus, PhotoCaptureDownloadArtifactsLabelNames } from '../enums';
import { GPCLoaderConstants, IntelligentPhotoCaptureCameraConstants, ODOMETER_MATCH_THRESHOLD, PhotoCaptureConstants } from '../constants';
const translateFn = TranslationService.translate;
const normalizeAlphaNumeric = (value) => value.replace(/[^a-z0-9]/gi, EMPTY_TEXT).toUpperCase();
const normalizeNumeric = (value) => value.replace(/[^0-9.]/g, EMPTY_TEXT);
const hasValue = (value) => !!(value === null || value === void 0 ? void 0 : value.trim());
const isOdometerWithinThreshold = (detectedText, odometer) => {
    if (odometer == null) {
        return false;
    }
    const ocrOdometerValue = Number.parseFloat(normalizeNumeric(detectedText));
    return !Number.isNaN(ocrOdometerValue) && Math.abs(ocrOdometerValue - odometer) <= ODOMETER_MATCH_THRESHOLD;
};
export const identifyAsMovableFolderTypeCodes = [
    IntelligentPhotoCaptureArtifactsCategoryNames.PRIOR_DAMAGE,
    IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_DAMAGE,
    IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS
];
/**
 * Utility function to create icon tags for vehicle information validation status
 * @param isMatch Indicates whether the detected information matches the vehicle information
 * @param isEditedByUser Indicates whether the information was edited by the user, which would override match status
 * @return {ITagItem} An object containing the text, icon, and icon color to represent the validation status of the vehicle information
 */
const createIconTagsForVehicle = (isMatch, isEditedByUser) => {
    const statusTextKey = isEditedByUser
        ? IntelligentPhotoCaptureDecodeConstants.VERIFIED_TEXT_KEY
        : isMatch
            ? IntelligentPhotoCaptureDecodeConstants.MATCHES_JOB_TEXT_KEY
            : IntelligentPhotoCaptureDecodeConstants.NEEDS_INTERVENTION_TEXT_KEY;
    const statusIcon = isEditedByUser
        ? IntelligentPhotoCaptureDecodeConstants.COMPLETE_ICON
        : isMatch
            ? IntelligentPhotoCaptureDecodeConstants.COMPLETE_ICON
            : IntelligentAssignmentSummary.MI_ICON_ALERT_FILLED_TRIANGLE;
    const statusIconColor = isEditedByUser
        ? IntelligentPhotoCaptureDecodeConstants.MATCHED_STATUS_ICON_COLOR
        : isMatch
            ? IntelligentPhotoCaptureDecodeConstants.MATCHED_STATUS_ICON_COLOR
            : IntelligentPhotoCaptureDecodeConstants.MISMATCHED_STATUS_ICON_COLOR;
    return {
        text: translateFn(statusTextKey),
        type: TagType.TEXT,
        icon: statusIcon,
        iconColor: statusIconColor
    };
};
const excludedUploadFileTypesSet = new Set([
    ...PhotoCaptureAllowedMimeTypeConstant,
    ...IntelligentPhotoCaptureExcludedFileTypes
]);
export const getCategoryToFileTypesMapping = () => ({
    [IntelligentPhotoCaptureArtifactsCategoryNames.COLLECTING_ARTIFACTS]: [],
    [IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION]: [
        IntelligentPhotoCaptureEstimatorContentType.VIN,
        IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER,
        IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE,
        IntelligentPhotoCaptureEstimatorContentType.ODOMETER
    ],
    [IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS]: [
        IntelligentPhotoCaptureEstimatorContentType.FRONTLEFT,
        IntelligentPhotoCaptureEstimatorContentType.FRONTRIGHT,
        IntelligentPhotoCaptureEstimatorContentType.REARLEFT,
        IntelligentPhotoCaptureEstimatorContentType.REARRIGHT
    ],
    [IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_DAMAGE]: [IntelligentPhotoCaptureEstimatorContentType.DAMAGE],
    [IntelligentPhotoCaptureArtifactsCategoryNames.PRIOR_DAMAGE]: [],
    [IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS]: []
});
export var albumOrganizer;
(function (albumOrganizer) {
    /**
     * Organizes images into categorized albums based on file descriptions
     * @param attachmentInfo Array of photo attachment information
     * @param countryCode Country code for localization
     * @param screenName Current screen name to detect specific workflows
     * @param shouldDisplayDefaultCategory Flag indicating whether to display a default category when no attachments are present
     * @param photoCaptureVehicle Optional vehicle information for the photo capture context, which may be used for personalized album organization or tagging
     * @param isConfidence Optional flag indicating if confidence level should be considered for album organization or tagging
     * @param prtcDetailsData Optional PRTC details data that may be used for personalized album organization or tagging in PRTC scenarios
     * @param userVehicleInfo Optional user-specific vehicle information that may be used for personalized album organization or tagging if available
     * @returns {IIntelligentPhotoCaptureAlbumHierarchyDataProps} Object containing organized albums and album document IDs
     */
    albumOrganizer.organizeImagesIntoAlbums = (attachmentInfo, countryCode, screenName, shouldDisplayDefaultCategory, photoCaptureVehicle, isConfidence, prtcDetailsData, userVehicleInfo) => {
        const albums = [];
        const albumDocIds = [];
        if (!attachmentInfo.length && shouldDisplayDefaultCategory) {
            const category = IntelligentPhotoCaptureArtifactsCategoryNames.COLLECTING_ARTIFACTS;
            return {
                albums: [
                    {
                        header: getAlbumHeader(category),
                        folderTypeCode: category,
                        id: '0',
                        shouldShowAlbum: true,
                        subAlbums: [],
                        tiles: []
                    }
                ],
                albumDocIds: []
            };
        }
        // Process attachments to create tiles based on folderTypeCode
        attachmentInfo.forEach((attachment, index) => {
            const { foldersModelInfo } = attachment;
            if (foldersModelInfo && foldersModelInfo.length) {
                foldersModelInfo.forEach((folderModel) => {
                    const matchingAlbum = albums.find((album) => album.folderTypeCode === folderModel.folderTypeCode);
                    if (!matchingAlbum) {
                        // Create a new album if it doesn't exist
                        const newAlbum = createNewAlbum(albums.length, getAlbumHeader(folderModel.folderTypeCode), folderModel.folderTypeCode);
                        albums.push(newAlbum);
                    }
                    // Add tile to the matching album
                    const albumToUpdate = albums.find((album) => album.folderTypeCode === folderModel.folderTypeCode);
                    if (albumToUpdate) {
                        const parentTile = createTile(attachment, countryCode, false, index, screenName, folderModel, photoCaptureVehicle, isConfidence, prtcDetailsData, userVehicleInfo);
                        albumToUpdate.tiles.push(parentTile);
                        albumToUpdate.shouldShowAlbum = true;
                        albumDocIds.push({
                            tileId: Number(parentTile.id),
                            attachmentIdentifier: attachment.id,
                            fileId: attachment.fileId,
                            thumbFileId: attachment.thumbFileId,
                            parentQuestionId: attachment.parentQuestionId,
                            questionnaireQuestionId: attachment.questionnaireQuestionId
                        });
                    }
                });
            }
            else {
                const category = IntelligentPhotoCaptureArtifactsCategoryNames.COLLECTING_ARTIFACTS;
                // Find or create the appropriate album
                let albumToUpdate = albums.find((album) => album.folderTypeCode === category);
                if (!albumToUpdate) {
                    const newAlbum = createNewAlbum(albums.length, getAlbumHeader(category), category);
                    albums.push(newAlbum);
                    albumToUpdate = newAlbum;
                }
                // Add tile to the album
                if (albumToUpdate) {
                    const parentTile = createTile(attachment, countryCode, false, index, screenName, undefined, undefined, isConfidence, prtcDetailsData, userVehicleInfo);
                    albumToUpdate.tiles.push(parentTile);
                    albumToUpdate.shouldShowAlbum = true;
                    albumDocIds.push({
                        tileId: Number(parentTile.id),
                        attachmentIdentifier: attachment.id,
                        fileId: attachment.fileId,
                        thumbFileId: attachment.thumbFileId,
                        parentQuestionId: attachment.parentQuestionId,
                        questionnaireQuestionId: attachment.questionnaireQuestionId
                    });
                }
            }
        });
        // Sort albums based on the order defined in getCategoryToFileTypesMapping
        const categoryOrder = Object.keys(getCategoryToFileTypesMapping());
        albums.sort((item1, item2) => {
            const indexA = categoryOrder.indexOf(item1.folderTypeCode || EMPTY_TEXT);
            const indexB = categoryOrder.indexOf(item2.folderTypeCode || EMPTY_TEXT);
            return indexA - indexB;
        });
        return {
            albums,
            albumDocIds
        };
    };
    /**
     * Creates a new album object with the specified header and category
     * @param albumLength Current number of albums, used to generate a unique ID for the new album
     * @param header The display header/title for the album
     * @param category The category/folderTypeCode associated with the album, used for organizing images
     * @returns {IPhotoCaptureNestedAlbum} A new album object with initialized properties
     */
    const createNewAlbum = (albumLength, header, category) => ({
        header,
        folderTypeCode: category,
        id: albumLength.toString(),
        shouldShowAlbum: false,
        subAlbums: [],
        tiles: []
    });
    /**
     * Creates a tile object representing an image in the UI
     * @param attachment Image attachment info
     * @param countryCode Country code for localization
     * @param isPrimaryImage Whether this is a parent/primary image
     * @param index Index for generating unique IDs
     * @param screenName Current screen name to detect specific workflows
     * @param folderModel The folder model information for the album
     * @param photoCaptureVehicle Optional vehicle information for the photo capture context
     * @param isConfidence Optional flag indicating if confidence level should be considered
     * @param prtcDetailsData Optional PRTC details data for the tile
     * @param userVehicleInfo Optional user-specific vehicle information that may be used for validation if available
     * @returns {IIntelligentPhotoCaptureMultiTaggedProps} A configured tile object
     */
    const createTile = (attachment, countryCode, isPrimaryImage, index, screenName, folderModel, photoCaptureVehicle, isConfidence, prtcDetailsData, userVehicleInfo) => {
        const tags = createTagsforTile(attachment, folderModel, countryCode, isConfidence, prtcDetailsData);
        const verificationStatusTags = createVerificationStatusDisplay(folderModel, countryCode, photoCaptureVehicle, userVehicleInfo);
        tags.push(...verificationStatusTags);
        const shouldUseHorizontalLayout = shouldUseHorizontalTileLayout(screenName, (folderModel === null || folderModel === void 0 ? void 0 : folderModel.folderTypeCode) || EMPTY_TEXT);
        return {
            id: attachment.id,
            contentType: folderModel === null || folderModel === void 0 ? void 0 : folderModel.contentType,
            url: EnterpriseDocStoreService.createFileUrl(attachment.thumbFileId || attachment.fileId),
            fileName: attachment.imageName,
            mimeType: attachment.mimeType,
            isShared: false,
            isShareable: false,
            isReceived: false,
            isSent: false,
            isSelected: false,
            isSelectModeOn: true,
            isPrimaryImage,
            index: index,
            height: shouldUseHorizontalLayout
                ? IntelligentAlbumHierarchy.HORIZONTAL_TILE_HEIGHT
                : IntelligentAlbumHierarchy.SMALL_TILE_HEIGHT,
            width: shouldUseHorizontalLayout
                ? IntelligentAlbumHierarchy.HORIZONTAL_TILE_WIDTH
                : IntelligentAlbumHierarchy.SMALL_TILE_WIDTH,
            tags: tags && tags.length ? tags : [],
            orientation: shouldUseHorizontalLayout ? TileOrientation.HORIZONTAL : TileOrientation.VERTICAL,
            warningTypeIcon: attachment.iqaQualityStatus === IntelligentPhotoCaptureIQAQualityStatus.FAIL
                ? IntelligentAssignmentSummary.MI_ICON_ALERT_FILLED_TRIANGLE
                : undefined
        };
    };
    /**
     * Creates tags for image tiles based on file description
     * @param attachment Image attachment info
     * @param folderModel The folder model information for the album
     * @param countryCode Country code for localization
     * @param isConfidence Optional flag indicating if confidence level should be considered for tagging
     * @param prtcDetailsData Optional PRTC details data that may be used for personalized tagging in PRTC scenarios
     * @returns {ITagItem[]} Array of tag items
     */
    const createTagsforTile = (attachment, folderModel, countryCode, isConfidence, prtcDetailsData) => {
        if (!folderModel)
            return [];
        const contentTypeToLabelMap = getContentTypeToLabelMapping(countryCode);
        const labelKey = folderModel.contentType
            ? contentTypeToLabelMap[folderModel.contentType]
            : undefined;
        const result = [];
        labelKey && result.push({ text: translateFn(labelKey), type: TagType.TEXT });
        folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            result.push({ text: folderModel.text || EMPTY_TEXT, type: TagType.ALLOW_EDIT });
        if (folderModel.labelTypeCode === IntelligentPhotoCaptureEstimatorLabelTypeCode.PRTC) {
            const prtcDescription = getPrtcDescriptionFromData(folderModel.text, prtcDetailsData);
            prtcDescription &&
                result.push({
                    text: prtcDescription,
                    type: TagType.TEXT
                });
        }
        if (isConfidence && folderModel.confidence != null) {
            result.push({ text: folderModel.confidence.toString(), type: TagType.TEXT });
        }
        if (folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS) {
            if (!attachmentInfo.isImageMimeType(attachment.mimeType)) {
                result.push({ text: attachment.imageName, type: TagType.TEXT });
            }
        }
        return result;
    };
    /**
     * Creates verification status display metadata by validating detected text against vehicle information
     * @param folderModel The folder model containing detected text and content type (license plate, odometer, or VIN)
     * @param countryCode Country code for localization, which may be used for region-specific validation rules or formatting
     * @param photoCaptureVehicle The vehicle information to validate detected text against
     * @param userVehicleInfo Optional user-specific vehicle information that may be used for validation if available
     * @returns {ITagItem[]} Array of tag metadata containing status text/icon details.
     * Returns an empty array if validation cannot be performed due to missing data or unsupported content type.
     */
    const createVerificationStatusDisplay = (folderModel, countryCode = CountryCode.UNITED_STATES, photoCaptureVehicle, userVehicleInfo) => {
        if (!folderModel ||
            folderModel.folderTypeCode !== IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION) {
            return [];
        }
        const isEditedByUser = folderModel.isImageEdited || false;
        const normalizedDetectedText = (folderModel.text || EMPTY_TEXT).trim();
        const { contentType } = folderModel;
        if (contentType === IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE ||
            contentType === IntelligentPhotoCaptureEstimatorContentType.ODOMETER) {
            return handleLicensePlateOdometerVerification(contentType, normalizedDetectedText, isEditedByUser, photoCaptureVehicle);
        }
        if (contentType === IntelligentPhotoCaptureEstimatorContentType.VIN ||
            contentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER) {
            return handleVinVerification(countryCode, isEditedByUser, normalizedDetectedText, photoCaptureVehicle, userVehicleInfo);
        }
        return [];
    };
    /**
     * Builds verification tags for detected license plate and odometer values.
     * @param contentType Vehicle information content type to validate.
     * @param normalizedDetectedText Trimmed OCR text detected from the folder model.
     * @param isEditedByUser Indicates whether the detected value was manually edited by the user.
     * @param photoCaptureVehicle Connected vehicle data used as the source of truth for validation.
     * @returns {ITagItem[]} A single verification tag when the value can be validated; otherwise an empty array.
     */
    const handleLicensePlateOdometerVerification = (contentType, normalizedDetectedText, isEditedByUser, photoCaptureVehicle) => {
        if (isEditedByUser)
            return [createIconTagsForVehicle(false, isEditedByUser)];
        if (!hasValue(normalizedDetectedText)) {
            return [createIconTagsForVehicle(false, false)];
        }
        if (contentType === IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE) {
            const licensePlate = photoCaptureVehicle === null || photoCaptureVehicle === void 0 ? void 0 : photoCaptureVehicle.licensePlate;
            if (!hasValue(licensePlate)) {
                return [];
            }
            const isMatch = normalizeAlphaNumeric(normalizedDetectedText) === normalizeAlphaNumeric(licensePlate);
            return [createIconTagsForVehicle(isMatch, isEditedByUser)];
        }
        if ((photoCaptureVehicle === null || photoCaptureVehicle === void 0 ? void 0 : photoCaptureVehicle.odometer) == null) {
            return [];
        }
        const isMatch = isOdometerWithinThreshold(normalizedDetectedText, photoCaptureVehicle.odometer);
        return [createIconTagsForVehicle(isMatch, isEditedByUser)];
    };
    /**
     * Creates VIN decode status tags based on the upstream VIN decode result.
     * @param upstreamVinStatus VIN decode status returned from upstream processing.
     * @returns {ITagItem[]} A single decoded or needs-intervention tag when the status is recognized; otherwise an empty array.
     */
    const getVinDecodeStatusTags = (upstreamVinStatus) => {
        if (upstreamVinStatus === IntelligentPhotoCaptureVinDecodeStatus.FULL) {
            return [
                {
                    text: translateFn(IntelligentPhotoCaptureDecodeConstants.DECODED_TEXT_KEY),
                    type: TagType.TEXT,
                    icon: IntelligentPhotoCaptureDecodeConstants.COMPLETE_ICON,
                    iconColor: IntelligentPhotoCaptureDecodeConstants.MATCHED_STATUS_ICON_COLOR
                }
            ];
        }
        if (upstreamVinStatus === IntelligentPhotoCaptureVinDecodeStatus.PARTIAL) {
            return [
                {
                    text: translateFn(IntelligentPhotoCaptureDecodeConstants.NEEDS_INTERVENTION_TEXT_KEY),
                    type: TagType.TEXT,
                    icon: IntelligentAssignmentSummary.MI_ICON_ALERT_FILLED_TRIANGLE,
                    iconColor: IntelligentPhotoCaptureDecodeConstants.MISMATCHED_STATUS_ICON_COLOR
                }
            ];
        }
        return [];
    };
    /**
     * Builds VIN verification and decode-status tags for vehicle information tiles.
     * @param countryCode Country code used when resolving upstream VIN decode status.
     * @param isEditedByUser Indicates whether the VIN text was manually edited by the user.
     * @param normalizedDetectedText Trimmed VIN text detected from the folder model.
     * @param photoCaptureVehicle Connected vehicle used for VIN match validation when available.
     * @param userVehicleInfo Optional user-updated vehicle information used to resolve decode status for edited VIN values.
     * @returns {ITagItem[]} Verification tags for VIN match and decode status. Returns a needs-intervention
     * tag when a connected vehicle exists without vinNo and detected VIN text is empty (and not user-edited);
     * returns an empty array when no validation/decode state can be derived.
     */
    const handleVinVerification = (countryCode, isEditedByUser, normalizedDetectedText, photoCaptureVehicle, userVehicleInfo) => {
        const updateVehicleInfo = userVehicleInfo
            ? Object.assign(Object.assign({}, userVehicleInfo), { vinNo: normalizedDetectedText }) : photoCaptureVehicle;
        const upstreamVinStatus = updateVehicleInfo
            ? getUpstreamVinStatus(updateVehicleInfo.vinNo, countryCode, updateVehicleInfo)
            : undefined;
        const result = [];
        if (isEditedByUser) {
            result.push(createIconTagsForVehicle(false, isEditedByUser));
        }
        if (!isEditedByUser && !(photoCaptureVehicle === null || photoCaptureVehicle === void 0 ? void 0 : photoCaptureVehicle.vinNo) && !hasValue(normalizedDetectedText)) {
            return [createIconTagsForVehicle(false, false)];
        }
        if (photoCaptureVehicle && photoCaptureVehicle.vinNo) {
            let isMatch = false;
            if (!isEditedByUser && hasValue(photoCaptureVehicle.vinNo)) {
                isMatch = normalizeAlphaNumeric(normalizedDetectedText) === normalizeAlphaNumeric(photoCaptureVehicle.vinNo);
                result.push(createIconTagsForVehicle(isMatch, isEditedByUser));
            }
            if (!isEditedByUser && !isMatch) {
                return result;
            }
        }
        result.push(...getVinDecodeStatusTags(upstreamVinStatus));
        return result;
    };
    const shouldUseHorizontalTileLayout = (screenName, albumHeader) => screenName === IntelligentAssignmentSummary.SUMMARY &&
        albumHeader === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION;
})(albumOrganizer || (albumOrganizer = {}));
export var attachmentBatchActivityInfo;
(function (attachmentBatchActivityInfo_1) {
    /**
     * Creates batch activity info for a given batch of attachments, generating unique identifiers for each image and structuring the data according to the IIntelligentPhotoCaptureAttachmentBatchInfo interface.
     * @param batchId - The unique identifier for the batch of attachments
     * @param files - An array of IFile objects representing the files in the batch, which may contain document UIDs used for generating S3 URLs
     * @param attachmentInfo - A 2D array of IPhotoCaptureAttachmentFileInfo objects representing existing attachment information, used to ensure generated unique identifiers do not collide with existing ones
     * @return An object conforming to the IIntelligentPhotoCaptureAttachmentBatchInfo interface, containing the batch ID, rendering status, batch status, and an array of attachment references with unique identifiers and document UIDs
     */
    attachmentBatchActivityInfo_1.createBatchActivityInfo = (batchId, files, attachmentInfo) => {
        const attachments = [];
        // Use forEach to build the attachments array incrementally
        files.forEach((file) => {
            attachments.push({
                imageUniqueIdentifier: generateUniqueIdentifier(attachmentInfo, attachments),
                docUID: file.docUID || EMPTY_TEXT,
                uri: EMPTY_TEXT,
                fileSize: file.fileSizeInBytes,
                originalFileName: file.fileName
            });
        });
        const batchInfo = {
            batchId: batchId,
            isRenderingCompleted: false,
            batchStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING,
            attachments
        };
        return batchInfo;
    };
    /**
     * Generates a unique identifier for an image
     * @param attachmentInfo - Array of attachment file information to check against for uniqueness
     * @param currentAttachments - Current batch attachments that have already been processed
     * @returns A unique numeric identifier that doesn't exist in attachmentInfo or currentAttachments
     */
    const generateUniqueIdentifier = (attachmentInfo, currentAttachments) => {
        let uniqueIdentifier;
        do {
            uniqueIdentifier = new Date().getUTCMilliseconds();
        } while (
        // Avoid 0 as it could be falsy/default value
        uniqueIdentifier === 0 ||
            // Check if ID exists in attachmentInfo or current batch attachments
            attachmentInfo.find((image) => image[0].id === uniqueIdentifier) ||
            (currentAttachments &&
                currentAttachments.find((attachment) => attachment.imageUniqueIdentifier === uniqueIdentifier)));
        return uniqueIdentifier;
    };
    /**
     * Fetches the S3 URL for a given document UID.
     * @param imageDocUID - The document UID of the image.
     * @returns A Promise that resolves to the S3 URL of the image.
     */
    attachmentBatchActivityInfo_1.gets3UrlForDocUID = (imageDocUID) => __awaiter(this, void 0, void 0, function* () { return yield EnterpriseDocStoreService.getFileS3URL(imageDocUID); });
    /**
     * Updates the batch activity info when an image analysis batchStatus is completed.
     * @param attachmentBatchActivityInfo - The current array of batch activity info
     * @param batchId - The ID of the batch to update
     * @return An updated array of batch activity info with the specified batch's batchStatus set to COMPLETED
     */
    attachmentBatchActivityInfo_1.updateBatchActivityInfo = (attachmentBatchActivityInfo, batchId) => {
        return attachmentBatchActivityInfo.map((batchInfo) => {
            if (batchInfo.batchId === batchId) {
                return Object.assign(Object.assign({}, batchInfo), { batchStatus: IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED });
            }
            return batchInfo;
        });
    };
})(attachmentBatchActivityInfo || (attachmentBatchActivityInfo = {}));
export var attachmentInfo;
(function (attachmentInfo_1) {
    /**
     * Extracts PRTC codes from attachment information by iterating through the attachment groups and their folder model info, specifically looking for folder types related to vehicle damage and collecting unique PRTC text values.
     * @param attachmentInfo - The attachment information containing groups of attachments, where each attachment may have folder model info with potential PRTC codes in the text field.
     * @returns An array of unique PRTC codes extracted from the attachment information.
     * The function iterates through each group of attachments, then through each attachment's folder model info, checking for the VEHICLE_DAMAGE folder type. If found, it extracts the text value as a potential PRTC code and adds it to the result array if it's not already included, ensuring uniqueness.
     */
    attachmentInfo_1.extractPrtcCodesFromAttachmentInfo = (attachmentInfo) => {
        const prtcCodes = new Set();
        const addCode = (code) => {
            prtcCodes.add(code);
        };
        attachmentInfo.forEach(group => {
            group.forEach(attachment => {
                (attachment.foldersModelInfo || []).forEach(folderModel => {
                    if (folderModel.labelTypeCode !== IntelligentPhotoCaptureEstimatorLabelTypeCode.PRTC) {
                        return;
                    }
                    const text = folderModel.text || '';
                    if (!text) {
                        return;
                    }
                    addCode(text);
                });
            });
        });
        return Array.from(prtcCodes);
    };
    /**
     * Computes the vehicle information update for an attachment based on the analyze response content, normalizing the vehicle info and handling cases where vehicle info is null or undefined.
     * @param contentWithVehicleInfoProperty - The analyze response content that may contain the vehicleInfo property, which can be an object with vehicle details, null (indicating no vehicle info found), or undefined (indicating vehicle info was not analyzed)
     * @returns Object containing the updated vehicleInfo property for the attachment
     */
    const computeVehicleInfoUpdate = (contentWithVehicleInfoProperty) => {
        var _a, _b;
        if (!contentWithVehicleInfoProperty) {
            return {};
        }
        if (contentWithVehicleInfoProperty.vehicleInfo === null) {
            return { vehicleInfo: null };
        }
        if (!contentWithVehicleInfoProperty.vehicleInfo) {
            return {};
        }
        const normalizedVehicleInfo = Object.assign(Object.assign({}, contentWithVehicleInfoProperty.vehicleInfo), { statusCode: (_a = contentWithVehicleInfoProperty.vehicleInfo.statusCode) !== null && _a !== void 0 ? _a : null, failureReason: (_b = contentWithVehicleInfoProperty.vehicleInfo.failureReason) !== null && _b !== void 0 ? _b : null });
        delete normalizedVehicleInfo.empty;
        return { vehicleInfo: normalizedVehicleInfo };
    };
    /**
     * Computes estimatorVehicleInfo update for an attachment.
     * Priority:
     * 1) Use estimatorVehicleInfo from analyzer content when present.
     * 2) If missing, compare detected VIN text with connected vehicle VIN.
     *    When VIN matches, use connected vehicle data as estimatorVehicleInfo.
     * @param contentWithEstimatorVehicleInfoProperty - Analyzer content that may include estimatorVehicleInfo
     * @param connectedVehicle - Connected vehicle from Redux state used for VIN-based fallback
     * @param detectedVin - VIN text detected by analyzer content
     * @returns Object containing estimatorVehicleInfo update when available; otherwise empty object
     * Selective mapping applied only to connectedVehicle fallback data to exclude identification
     * fields (vinNo, licensePlate, odometer) from estimatorVehicleInfo. Analyzer-provided
     * estimatorVehicleInfo is used directly since it contains only estimation-relevant fields.
     * Update this mapping carefully when adding new fields to avoid mixing identification
     * and estimation data.
     */
    const computeEstimatorVehicleInfoUpdate = (contentWithEstimatorVehicleInfoProperty, connectedVehicle, detectedVin) => {
        if (contentWithEstimatorVehicleInfoProperty === null || contentWithEstimatorVehicleInfoProperty === void 0 ? void 0 : contentWithEstimatorVehicleInfoProperty.estimatorVehicleInfo) {
            return {
                estimatorVehicleInfo: Object.assign({}, contentWithEstimatorVehicleInfoProperty.estimatorVehicleInfo)
            };
        }
        if (!hasValue(detectedVin) || !connectedVehicle || !hasValue(connectedVehicle.vinNo)) {
            return {};
        }
        const isVinMatched = normalizeAlphaNumeric(detectedVin) === normalizeAlphaNumeric(connectedVehicle.vinNo);
        if (!isVinMatched) {
            return {};
        }
        return {
            estimatorVehicleInfo: {
                makeModel: connectedVehicle.makeModel,
                makeId: connectedVehicle.makeId,
                modelDesc: connectedVehicle.modelDesc,
                modelId: connectedVehicle.modelId,
                modelYear: connectedVehicle.modelYear,
                mitchellVid: connectedVehicle.mitchellVid,
                pinVid: connectedVehicle.pinVid,
                nadaVid: connectedVehicle.nadaVid,
                redbookVid: connectedVehicle.redbookVid,
                serviceBarcode: connectedVehicle.serviceBarcode
            }
        };
    };
    /**
     * Creates lookup maps from stored activity batch data and estimated files info
     * @param attachmentBatchActivity - Batch activity info containing docUID to identifier mappings
     * @param filesInfo - Array of file info containing parent-child relationships
     * @returns Object containing docUID to identifier map and parent-child relationship map
     */
    const createLookupMaps = (attachmentBatchActivity, filesInfo) => {
        // Create a map of docUID to imageUniqueIdentifier for quick lookup
        const docUidToIdentifierMap = new Map();
        attachmentBatchActivity.forEach((batchInfo) => {
            batchInfo.attachments.forEach((attachment) => {
                docUidToIdentifierMap.set(attachment.docUID, attachment.imageUniqueIdentifier);
            });
        });
        // Create a map of parent file id to thumbnail file info
        const parentIdToThumbnailMap = new Map();
        filesInfo.forEach((fileInfo) => {
            if (fileInfo.parentId) {
                parentIdToThumbnailMap.set(fileInfo.parentId, fileInfo);
            }
        });
        return { docUidToIdentifierMap, parentIdToThumbnailMap };
    };
    /**
     * Converts a batch of files to photo capture attachment requests.
     * @param files - Array of files to convert to attachment requests
     * @param attachmentBatchActivity - Batch activity info containing docUID to identifier mappings
     * @param filesInfo - Array of file info containing parent-child relationships
     * @returns Array of arrays of photo capture attachment requests
     */
    attachmentInfo_1.createAttachments = (files, attachmentBatchActivity, filesInfo) => {
        const { docUidToIdentifierMap, parentIdToThumbnailMap } = createLookupMaps(attachmentBatchActivity, filesInfo);
        return files.map((file) => {
            // Get thumbnail info from parent-child relationship or fallback to file.thumbnail
            const thumbnailFile = parentIdToThumbnailMap.get(file.id);
            const thumbnailDocUId = (thumbnailFile === null || thumbnailFile === void 0 ? void 0 : thumbnailFile.docUID) || EMPTY_TEXT;
            const fileId = file.docUID || EMPTY_TEXT;
            // Return an array containing a single attachment object for each file
            return [
                {
                    attachmentId: 0,
                    thumbNailAttachmentId: 0,
                    fileId: fileId,
                    thumbFileId: thumbnailDocUId || fileId,
                    id: docUidToIdentifierMap.get(fileId) || new Date().getUTCMilliseconds(),
                    author: null,
                    fileDescription: EMPTY_TEXT,
                    fullImgUrl: null,
                    imageName: file.fileName,
                    mimeType: getMimeType(MimeTypeService.lookup(file.fileName)),
                    screenName: EMPTY_TEXT,
                    source: null,
                    thumbUrl: null,
                    parentQuestionId: 0,
                    questionnaireQuestionId: 0,
                    documentId: 0,
                    imageSize: file.fileSizeInBytes,
                    thumbnailImageSize: (thumbnailFile === null || thumbnailFile === void 0 ? void 0 : thumbnailFile.fileSizeInBytes) || file.fileSizeInBytes,
                    fileReferenceId: file.docId,
                    thumbFileReferenceId: (thumbnailFile === null || thumbnailFile === void 0 ? void 0 : thumbnailFile.docId) || file.docId,
                    historyInfo: [
                        {
                            indexId: 0,
                            docStoreId: file.docUID || EMPTY_TEXT,
                            imageDateTime: formatDateTime(new Date())
                        }
                    ]
                }
            ];
        });
    };
    /**
     * Updates the tempAttachmentInfo in the state based on the analyze response payload.
     * @param tempAttachmentInfo - The current tempAttachmentInfo data.
     * @param payload - The analyze response payload.
     * @returns Updated tempAttachmentInfo.
     */
    attachmentInfo_1.updateAttachmentsInfo = (tempAttachmentInfo, payload, connectedVehicle, vinOption) => {
        const categoryToFileTypesMapping = getCategoryToFileTypesMapping();
        return tempAttachmentInfo.map((attachmentGroup) => attachmentGroup.map((attachment) => {
            const matchingAttachment = payload.attachments.find((responseAttachment) => responseAttachment.imageUniqueIdentifier === attachment.id);
            if (!matchingAttachment) {
                return attachment;
            }
            const updatedHistoryInfo = [...(attachment.historyInfo || [])];
            const baseHistoryInfo = updatedHistoryInfo[0] || {
                indexId: 0,
                docStoreId: attachment.fileId || EMPTY_TEXT,
                documentId: attachment.documentId
            };
            updatedHistoryInfo[0] = Object.assign(Object.assign({}, baseHistoryInfo), { iqaBlurScore: matchingAttachment.blurScore, iqaBrightnessScore: matchingAttachment.brightnessScore, iqaContrastScore: matchingAttachment.contrastScore, iqaGammaScore: matchingAttachment.gammaScore, iqaNoiseScore: matchingAttachment.noiseScore });
            const contentWithVehicleInfoProperty = matchingAttachment.contents.find((content) => Object.prototype.hasOwnProperty.call(content, 'vehicleInfo'));
            const vehicleInfoUpdate = computeVehicleInfoUpdate(contentWithVehicleInfoProperty);
            const contentWithEstimatorVehicleInfoProperty = matchingAttachment.contents.find((content) => Object.prototype.hasOwnProperty.call(content, 'estimatorVehicleInfo'));
            const contentWithVinProperty = matchingAttachment.contents.find((content) => {
                var _a, _b;
                return ((_a = content.contentType) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase()) ===
                    IntelligentPhotoCaptureEstimatorContentType.VIN.toLowerCase() ||
                    ((_b = content.contentType) === null || _b === void 0 ? void 0 : _b.trim().toLowerCase()) ===
                        IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER.toLowerCase();
            });
            const estimatorVehicleInfoUpdate = computeEstimatorVehicleInfoUpdate(contentWithEstimatorVehicleInfoProperty, connectedVehicle, contentWithVinProperty === null || contentWithVinProperty === void 0 ? void 0 : contentWithVinProperty.text);
            const foldersModelInfo = matchingAttachment
                .contents.length
                ? matchingAttachment.contents.flatMap((content) => {
                    const contentType = content.contentType;
                    const folderTypeCode = isVinLikeContentType(contentType)
                        ? getVinLikeFolderTypeCodeByOption(contentType, vinOption)
                        : Object.keys(categoryToFileTypesMapping).find((key) => categoryToFileTypesMapping[key].includes(content.contentType));
                    if (!folderTypeCode)
                        return [
                            {
                                folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS,
                                labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION
                            }
                        ];
                    return [
                        {
                            folderTypeCode: folderTypeCode,
                            contentType: content.contentType,
                            confidence: content.confidence,
                            text: content.text,
                            labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION
                        }
                    ];
                })
                : (() => {
                    return [
                        {
                            folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS,
                            labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION
                        }
                    ];
                })();
            return Object.assign(Object.assign(Object.assign(Object.assign({}, attachment), { foldersModelInfo, historyInfo: updatedHistoryInfo, iqaQualityStatus: matchingAttachment.iqaQualityStatus }), vehicleInfoUpdate), estimatorVehicleInfoUpdate);
        }));
    };
    /**
     * Updates the tempAttachmentInfo by merging it with the new photoCaptureAttachmentInfo, ensuring that any attachments that are still pending categorization (i.e., those that do not have a matching ID in the new photoCaptureAttachmentInfo) are retained in the updated tempAttachmentInfo. This allows for a seamless update of attachment information while preserving any attachments that have not yet completed processing.
     * @param tempAttachmentInfo - The current tempAttachmentInfo data that may contain attachments pending categorization
     * @param photoCaptureAttachmentInfo - The new attachment info data received from the photo capture analyze response, which may contain updated information for some attachments
     * @return An updated array of attachment info that includes the new information from photoCaptureAttachmentInfo while retaining any attachments from tempAttachmentInfo that do not have a match in photoCaptureAttachmentInfo (i.e., those that are still pending categorization)
     */
    attachmentInfo_1.updateTempAttachmentInfo = (tempAttachmentInfo, photoCaptureAttachmentInfo) => {
        const incomingAttachmentIds = new Set(photoCaptureAttachmentInfo.flatMap((attachmentGroup) => attachmentGroup.map((attachment) => attachment.id)));
        const remainingTempAttachmentInfo = tempAttachmentInfo
            .map((attachmentGroup) => attachmentGroup.filter((attachment) => !incomingAttachmentIds.has(attachment.id)))
            .filter((attachmentGroup) => attachmentGroup.length > 0);
        return [...photoCaptureAttachmentInfo, ...remainingTempAttachmentInfo];
    };
    /**
     * Adds default foldersModelInfo with ADDITIONAL_ARTIFACTS category to attachments that have empty or undefined foldersModelInfo.
     * @param photoCaptureAttachmentInfo - The photo capture attachment info array (2D array of attachment groups)
     * @returns Updated attachment info with default foldersModelInfo applied where needed
     */
    attachmentInfo_1.addDefaultFoldersModelInfo = (photoCaptureAttachmentInfo) => {
        return photoCaptureAttachmentInfo.map((attachmentGroup) => attachmentGroup.map((attachment) => {
            if (!attachment.foldersModelInfo || attachment.foldersModelInfo.length === 0) {
                return Object.assign(Object.assign({}, attachment), { foldersModelInfo: [
                        {
                            folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS,
                            labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION
                        }
                    ] });
            }
            return attachment;
        }));
    };
    /**
     * Attachments whose IQA quality status is FAIL and are not uncategorized.
     * @param photoCaptureAttachmentInfo - The 2D array of attachment groups to inspect
     * @returns Array of categorized attachments with a FAIL quality status
     */
    attachmentInfo_1.getFailedQualityAttachments = (photoCaptureAttachmentInfo) => photoCaptureAttachmentInfo
        .flatMap((attachmentGroup) => attachmentGroup)
        .filter((attachment) => attachment.iqaQualityStatus === IntelligentPhotoCaptureIQAQualityStatus.FAIL);
    /**
     * Removes from tempAttachmentInfo the groups belonging to a failed batch.
     * @param tempAttachmentInfo - The current 2D array of pending attachment groups
     * @param attachmentBatchActivityInfo - The full list of batch activity info
     * @param batchId - The ID of the failed batch
     * @returns Updated tempAttachmentInfo with the failed batch's groups removed
     */
    attachmentInfo_1.updateTempAttachmentInfoOnFailure = (tempAttachmentInfo, attachmentBatchActivityInfoList, batchId) => {
        const failedBatch = attachmentBatchActivityInfoList.find((batchInfo) => batchInfo.batchId === batchId);
        if (!failedBatch) {
            return tempAttachmentInfo;
        }
        const failedIdentifiers = new Set(failedBatch.attachments.map((attachment) => attachment.imageUniqueIdentifier));
        return tempAttachmentInfo.map((tempAttachmentGroup) => tempAttachmentGroup.map((tempAttachment) => {
            if (!failedIdentifiers.has(tempAttachment.id)) {
                return tempAttachment;
            }
            return Object.assign(Object.assign({}, tempAttachment), { foldersModelInfo: [
                    {
                        folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS,
                        labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION
                    }
                ] });
        }));
    };
    attachmentInfo_1.isOCRMatchedWithVehicleInfo = (response, vehicleData, isVinMatched, isLicensePlateMatched, isOdometerMatched, vinOption) => {
        var _a;
        const ocrMatchResult = {
            isVinOcrMatched: isVinMatched,
            isLicensePlateOcrMatched: isLicensePlateMatched,
            isOdometerOcrMatched: isOdometerMatched
        };
        if (!vehicleData || !((_a = response.attachments) === null || _a === void 0 ? void 0 : _a.length)) {
            return ocrMatchResult;
        }
        for (const attachment of response.attachments) {
            for (const content of attachment.contents) {
                if (ocrMatchResult.isVinOcrMatched &&
                    ocrMatchResult.isLicensePlateOcrMatched &&
                    ocrMatchResult.isOdometerOcrMatched) {
                    return ocrMatchResult;
                }
                if (!hasValue(content.text)) {
                    continue;
                }
                const normalizedContentType = content.contentType.trim().toLowerCase();
                const detectedText = content.text.trim();
                switch (normalizedContentType) {
                    case IntelligentPhotoCaptureEstimatorContentType.VIN.toLowerCase():
                    case IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER.toLowerCase(): {
                        if ((vinOption === IntelligentPhotoCaptureVinOption.VIN &&
                            normalizedContentType !== IntelligentPhotoCaptureEstimatorContentType.VIN.toLowerCase()) ||
                            (vinOption === IntelligentPhotoCaptureVinOption.VIN_STICKER &&
                                normalizedContentType !== IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER.toLowerCase())) {
                            break;
                        }
                        if (ocrMatchResult.isVinOcrMatched || isVinMatched) {
                            break;
                        }
                        if (hasValue(vehicleData.vinNo) &&
                            normalizeAlphaNumeric(detectedText) === normalizeAlphaNumeric(vehicleData.vinNo)) {
                            ocrMatchResult.isVinOcrMatched = true;
                        }
                        break;
                    }
                    case 'licenseplate':
                    case IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE.toLowerCase(): {
                        if (ocrMatchResult.isLicensePlateOcrMatched || isLicensePlateMatched) {
                            break;
                        }
                        if (hasValue(vehicleData.licensePlate) &&
                            normalizeAlphaNumeric(detectedText) === normalizeAlphaNumeric(vehicleData.licensePlate)) {
                            ocrMatchResult.isLicensePlateOcrMatched = true;
                        }
                        break;
                    }
                    case 'odometer':
                    case IntelligentPhotoCaptureEstimatorContentType.ODOMETER.toLowerCase(): {
                        if (ocrMatchResult.isOdometerOcrMatched || isOdometerMatched || vehicleData.odometer == null) {
                            break;
                        }
                        if (isOdometerWithinThreshold(detectedText, vehicleData.odometer)) {
                            ocrMatchResult.isOdometerOcrMatched = true;
                        }
                        break;
                    }
                    default:
                        break;
                }
            }
        }
        return ocrMatchResult;
    };
    /**
     * Removes attachments by id from a 2D array of IPhotoCaptureAttachmentFileInfo
     * @param attachmentInfo 2D array of attachments
     * @param selectedAttachmentIds array of ids to remove
     * @returns filtered 2D array
     */
    function removeAttachmentsById(attachmentInfo, selectedAttachmentIds) {
        return attachmentInfo
            .map(group => group.filter(attachment => !selectedAttachmentIds.includes(attachment.id)))
            .filter(group => group.length > 0);
    }
    attachmentInfo_1.removeAttachmentsById = removeAttachmentsById;
    attachmentInfo_1.getIdentifyAsDestinationFolderTypeCodes = (tempAttachmentInfo, selectedAttachmentIds) => {
        if (!selectedAttachmentIds.length) {
            return [];
        }
        const selectedAttachments = tempAttachmentInfo
            .flat()
            .filter((attachment) => selectedAttachmentIds.includes(attachment.id));
        const movableSelectedAttachments = selectedAttachments.filter((attachment) => {
            var _a, _b;
            if (!attachmentInfo_1.isImageMimeType(attachment.mimeType)) {
                return false;
            }
            const attachmentFolderTypeCodes = (_b = (_a = attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.map(folderInfo => folderInfo.folderTypeCode)) !== null && _b !== void 0 ? _b : [];
            return attachmentFolderTypeCodes.some(folderTypeCode => identifyAsMovableFolderTypeCodes.includes(folderTypeCode));
        });
        // Disable if no movable attachments are in the selection
        if (movableSelectedAttachments.length === 0) {
            return [];
        }
        const movableSelectedFolderTypeCodes = new Set(movableSelectedAttachments.flatMap((attachment) => attachment.foldersModelInfo.map(folderInfo => folderInfo.folderTypeCode)));
        const selectedMovableFolderTypeCodes = identifyAsMovableFolderTypeCodes.filter((folderTypeCode) => movableSelectedFolderTypeCodes.has(folderTypeCode));
        if (selectedMovableFolderTypeCodes.length === 0 ||
            selectedMovableFolderTypeCodes.length === identifyAsMovableFolderTypeCodes.length) {
            return [];
        }
        return identifyAsMovableFolderTypeCodes.filter((folderTypeCode) => !movableSelectedFolderTypeCodes.has(folderTypeCode));
    };
    attachmentInfo_1.reidentifySelectedAttachmentsByFolderTypeCode = (tempAttachmentInfo, selectedAttachmentIds, destinationFolderTypeCode) => {
        if (!selectedAttachmentIds.length || !identifyAsMovableFolderTypeCodes.includes(destinationFolderTypeCode)) {
            return [];
        }
        const selectedIdsSet = new Set(selectedAttachmentIds);
        const updatedSelectedAttachments = [];
        for (const attachmentGroup of tempAttachmentInfo) {
            const selectedAttachmentsInGroup = attachmentGroup.filter(attachment => selectedIdsSet.has(attachment.id));
            if (selectedAttachmentsInGroup.length > 0) {
                const updatedAttachmentsInGroup = selectedAttachmentsInGroup.map(attachment => {
                    const hasMovableFolderTypeCode = (attachment.foldersModelInfo || []).some(folderInfo => identifyAsMovableFolderTypeCodes.includes(folderInfo.folderTypeCode));
                    // Only move if mimeType is an image
                    if (!hasMovableFolderTypeCode || !attachmentInfo_1.isImageMimeType(attachment.mimeType)) {
                        return attachment;
                    }
                    const preservedFolders = attachment.foldersModelInfo.filter(folderInfo => !identifyAsMovableFolderTypeCodes.includes(folderInfo.folderTypeCode));
                    const sourceMovableFolder = attachment.foldersModelInfo.find(folderInfo => identifyAsMovableFolderTypeCodes.includes(folderInfo.folderTypeCode));
                    return Object.assign(Object.assign({}, attachment), { foldersModelInfo: [
                            ...preservedFolders,
                            Object.assign(Object.assign(Object.assign({ folderTypeCode: destinationFolderTypeCode }, (sourceMovableFolder.text !== undefined && { text: sourceMovableFolder.text })), (sourceMovableFolder.contentType && { contentType: sourceMovableFolder.contentType })), (sourceMovableFolder.labelTypeCode && { labelTypeCode: sourceMovableFolder.labelTypeCode }))
                        ] });
                });
                updatedSelectedAttachments.push(updatedAttachmentsInGroup);
            }
        }
        return updatedSelectedAttachments;
    };
    /**
     * Finds an attachment by selected tile id from a 2D attachment collection.
     * @param attachmentInfo Two-dimensional attachment array
     * @param selectedTileId Selected tile id to match against attachment id
     * @returns Matching attachment or undefined when no match exists
     */
    attachmentInfo_1.getAttachmentBySelectedTileId = (attachmentInfo, selectedTileId) => attachmentInfo
        .flatMap((attachmentGroup) => attachmentGroup)
        .find((attachment) => attachment.id === selectedTileId);
    /**
     * Returns the folder text from an attachment for the matching content type.
     * @param attachment The selected attachment that may contain folder model entries.
     * @param contentType The expected content type to match against folder model info.
     * @returns Matching folder text when found; otherwise empty text.
     */
    attachmentInfo_1.getAttachmentTextByContentType = (attachment, contentType) => {
        var _a, _b;
        return ((_b = (_a = attachment === null || attachment === void 0 ? void 0 : attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.find((folderModel) => folderModel.contentType === contentType)) === null || _b === void 0 ? void 0 : _b.text) || EMPTY_TEXT;
    };
    /**
     * Returns an updated attachment with estimatorVehicleInfo and optional folder text updates.
     * @param attachment The attachment to update
     * @param contentType Content type used to locate and update matching folder model text
     * @param updatedText Text to set when a folder model matches the provided contentType
     * @param estimatorVehicleInfo The new estimator vehicle info to set for the attachment
     * @returns Updated attachment info with estimatorVehicleInfo and folder text updates, or undefined if attachment is invalid
     */
    attachmentInfo_1.updateAttachmentOCRDetailsById = (attachment, contentType, updatedText, estimatorVehicleInfo) => {
        var _a;
        if (!attachment) {
            return undefined;
        }
        const foldersModelInfo = (_a = attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.map((folderModel) => folderModel.contentType === contentType
            ? Object.assign(Object.assign({}, folderModel), { text: updatedText, isImageEdited: true }) : folderModel);
        const result = Object.assign(Object.assign({}, attachment), { foldersModelInfo });
        if (estimatorVehicleInfo !== undefined) {
            result.estimatorVehicleInfo = estimatorVehicleInfo;
        }
        return result;
    };
    /**
     * Applies delete and update patch operations to a 2D attachment collection.
     * @param attachmentInfo Existing 2D attachment collection to update
     * @param deletedAttachments Attachments to delete by id
     * @param updatedAttachments Updated attachment groups to upsert by attachment id
     * @returns Patched 2D attachment collection
     */
    attachmentInfo_1.patchUpdateAttachments = (attachmentInfo, deletedAttachments, updatedAttachments) => {
        const deleteIds = new Set((deletedAttachments || []).map(item => item.id));
        const filteredAttachmentInfo = deleteIds.size
            ? attachmentInfo
                .map((group) => group.filter((attachment) => !deleteIds.has(attachment.id)))
                .filter((group) => group.length > 0)
            : attachmentInfo;
        if (!updatedAttachments || !updatedAttachments.length) {
            return filteredAttachmentInfo;
        }
        const attachmentById = new Map();
        [...filteredAttachmentInfo, ...updatedAttachments]
            .filter((group) => Array.isArray(group) && !!group[0])
            .forEach((group) => {
            attachmentById.set(group[0].id, group);
        });
        return Array.from(attachmentById.values());
    };
    /**
     * Resets OCR match flags when attachments containing specific content types are deleted.
     *
     * @param attachmentInfo - Current attachment information to check for content types
     * @param currentOCRMatches - Current OCR match status
     * @param deletedAttachments - Array of attachments that were deleted
     * @returns Updated OCR match status with flags reset based on deleted content
     */
    attachmentInfo_1.resetOCRMatchesOnDelete = (attachmentInfo, currentOCRMatches, deletedAttachments) => {
        if (!deletedAttachments || deletedAttachments.length === 0) {
            return currentOCRMatches;
        }
        // Create a set of deleted attachment IDs for faster lookup
        const deletedIds = new Set(deletedAttachments.map(item => item.id));
        return resetOCRMatchesForDeletedIds(attachmentInfo, currentOCRMatches, deletedIds);
    };
    /**
     * Computes deleted attachments by comparing current and updated attachment arrays,
     * then resets OCR match flags based on the deleted attachments.
     *
     * @param currentAttachmentInfo - Current 2D array of attachment information
     * @param updatedAttachmentInfo - Updated 2D array of attachment information
     * @param currentOCRMatches - Current OCR match status
     * @returns Updated OCR match status with flags reset based on deleted content
     */
    attachmentInfo_1.computeOCRMatchesAfterUpdate = (currentAttachmentInfo, updatedAttachmentInfo, currentOCRMatches) => {
        const updatedAttachmentIds = new Set(updatedAttachmentInfo.flat().map((attachment) => attachment.id));
        const deletedIds = new Set(currentAttachmentInfo
            .flat()
            .filter((attachment) => !updatedAttachmentIds.has(attachment.id))
            .map(attachment => attachment.id));
        if (deletedIds.size === 0) {
            return currentOCRMatches;
        }
        return resetOCRMatchesForDeletedIds(currentAttachmentInfo, currentOCRMatches, deletedIds);
    };
    /**
     * Helper function that resets OCR match flags for attachments with specific IDs
     *
     * @param attachmentInfo - Current attachment information to check for content types
     * @param currentOCRMatches - Current OCR match status
     * @param deletedIds - Set of IDs for deleted attachments
     * @returns Updated OCR match status with flags reset based on deleted content
     */
    const resetOCRMatchesForDeletedIds = (attachmentInfo, currentOCRMatches, deletedIds) => {
        // Initialize result with current values
        const result = Object.assign({}, currentOCRMatches);
        // Find all deleted attachments in the current attachment info
        const deletedAttachments = attachmentInfo
            .flat()
            .filter((attachment) => deletedIds.has(attachment.id));
        // Collect normalized content types only from VEHICLE_INFORMATION folders.
        const deletedVehicleInfoContentTypes = new Set(deletedAttachments.flatMap((attachment) => (attachment.foldersModelInfo || [])
            .filter((folderInfo) => folderInfo.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION)
            .map(folderInfo => folderInfo.contentType && folderInfo.contentType.trim().toLowerCase())
            .filter((contentType) => !!contentType)));
        // Get normalized content types from constants
        const vinContentType = IntelligentPhotoCaptureEstimatorContentType.VIN.toLowerCase();
        const vinStickerContentType = IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER.toLowerCase();
        const odometerContentType = IntelligentPhotoCaptureEstimatorContentType.ODOMETER.toLowerCase();
        const licensePlateContentType = IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE.toLowerCase();
        // Reset flags only if corresponding content types are found
        if (deletedVehicleInfoContentTypes.has(vinContentType) ||
            deletedVehicleInfoContentTypes.has(vinStickerContentType)) {
            result.isVinOcrMatched = false;
        }
        if (deletedVehicleInfoContentTypes.has(odometerContentType)) {
            result.isOdometerOcrMatched = false;
        }
        if (deletedVehicleInfoContentTypes.has(licensePlateContentType)) {
            result.isLicensePlateOcrMatched = false;
        }
        return result;
    };
    /**
     * Finds attachments that contain estimatorVehicleInfo and are categorized under VEHICLE_INFORMATION for potential updates with new vehicle info from VIN decode results.
     * @param attachmentInfo - The current attachment information, which may be a nested array of attachment groups. The function will search through all attachments to find those that have estimatorVehicleInfo and are categorized under VEHICLE_INFORMATION, as these are the attachments relevant for updating with new vehicle info from VIN decode results.
     * @returns An array of attachments that contain estimatorVehicleInfo and are categorized under VEHICLE_INFORMATION, which can be used for updates with new vehicle information. If no such attachments are found, returns an empty array.
     */
    attachmentInfo_1.getVehicleInfoFromAttachments = (attachmentInfo) => {
        return attachmentInfo
            .flat()
            .find((attachment) => {
            var _a;
            return attachment.estimatorVehicleInfo &&
                ((_a = attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.some(folder => folder.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION));
        });
    };
    /**
     * Returns true when any deleted attachment belongs to VEHICLE_INFORMATION
     * and has VIN as its content type.
     * @param attachmentInfo Current 2D attachment info collection from state
     * @param deletedAttachments Attachments selected for deletion
     * @returns true when a deleted VIN vehicle-information attachment exists; otherwise false
     */
    attachmentInfo_1.hasDeletedVinInVehicleInformation = (attachmentInfo, deletedAttachments) => {
        if (!(deletedAttachments === null || deletedAttachments === void 0 ? void 0 : deletedAttachments.length)) {
            return false;
        }
        const deletedIds = new Set(deletedAttachments.map(item => item.id));
        return attachmentInfo
            .flat()
            .filter((attachment) => deletedIds.has(attachment.id))
            .some((attachment) => (attachment.foldersModelInfo || []).some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            (folderModel.contentType === IntelligentPhotoCaptureEstimatorContentType.VIN ||
                folderModel.contentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER)));
    };
    /**
     * Gets OCR matches from attachment info compared to vehicle data.
     * @param attachmentInfo Current attachment info from state
     * @param vehicleData Vehicle data to compare against attachment folder text values
     * @returns OCR match flags derived from attachment metadata
     */
    attachmentInfo_1.getOCRMatchesFromAttachmentInfo = (attachmentInfo, vehicleData) => {
        const result = {
            isVinOcrMatched: false,
            isLicensePlateOcrMatched: false,
            isOdometerOcrMatched: false
        };
        const vehicleInfoFolders = attachmentInfo
            .flat()
            .flatMap((attachment) => attachment.foldersModelInfo || [])
            .filter((folder) => folder.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION);
        vehicleInfoFolders.forEach((folder) => {
            var _a;
            const folderText = (_a = folder.text) === null || _a === void 0 ? void 0 : _a.trim();
            switch (folder.contentType) {
                case IntelligentPhotoCaptureEstimatorContentType.VIN:
                case IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER:
                    if (vehicleData &&
                        hasValue(vehicleData.vinNo) &&
                        hasValue(folderText) &&
                        normalizeAlphaNumeric(folderText) === normalizeAlphaNumeric(vehicleData.vinNo)) {
                        result.isVinOcrMatched = true;
                    }
                    break;
                case IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE:
                    if (vehicleData &&
                        hasValue(vehicleData.licensePlate) &&
                        hasValue(folderText) &&
                        normalizeAlphaNumeric(folderText) === normalizeAlphaNumeric(vehicleData.licensePlate)) {
                        result.isLicensePlateOcrMatched = true;
                    }
                    break;
                case IntelligentPhotoCaptureEstimatorContentType.ODOMETER:
                    if (vehicleData &&
                        vehicleData.odometer != null &&
                        hasValue(folderText) &&
                        isOdometerWithinThreshold(folderText, vehicleData.odometer)) {
                        result.isOdometerOcrMatched = true;
                    }
                    break;
                default:
                    break;
            }
        });
        return result;
    };
    /**
     * Sets the corresponding OCR user verified flag to true
     * based on the given content type.
     * Used by reducer to update ocrUserVerification cleanly.
     * @param currentFlags - Existing OCR user verified flags from state
     * @param contentType - The content type to set flag for
     * @returns Updated IIntelligentPhotoCaptureOCRUserVerification with matching value set to true
     */
    attachmentInfo_1.setOCRUserVerifiedState = (currentFlags, contentType) => {
        switch (contentType) {
            case IntelligentPhotoCaptureEstimatorContentType.VIN:
            case IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER:
                return Object.assign(Object.assign({}, currentFlags), { isVinUserVerified: true });
            case IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE:
                return Object.assign(Object.assign({}, currentFlags), { isLicensePlateUserVerified: true });
            case IntelligentPhotoCaptureEstimatorContentType.ODOMETER:
                return Object.assign(Object.assign({}, currentFlags), { isOdometerUserVerified: true });
            default:
                return currentFlags;
        }
    };
    /**
     * Derives OCR user verified flags from attachment folder metadata.
     * A flag becomes true when an attachment contains VEHICLE_INFORMATION
     * with the corresponding VIN, LICENSEPLATE, or ODOMETER content type
     * and isImageEdited is true (user has manually verified the value).
     * Used on context refresh/reload to restore user verified state from
     * existing attachment data without requiring re-verification.
     * @param attachmentInfo Current attachment info from state or GPC context response
     * @returns OCR user verified flags derived from attachment metadata
     */
    attachmentInfo_1.getOCRUserVerifiedStateFromAttachmentInfo = (attachmentInfo) => {
        let result;
        const vehicleInfoFolders = attachmentInfo
            .flat()
            .flatMap((attachment) => attachment.foldersModelInfo || [])
            .filter((folder) => folder.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            folder.isImageEdited === true);
        vehicleInfoFolders.forEach((folder) => {
            result = attachmentInfo_1.setOCRUserVerifiedState(result, folder.contentType);
        });
        return result;
    };
    /**
     * Recomputes OCR user verification after delete operations using isImageEdited metadata.
     * @param attachmentInfo Attachment info to evaluate (post-patch recommended so updatedAttachments are reflected)
     * @param deletedAttachments Attachments selected for deletion (used to no-op when empty/undefined)
     * @param currentFlags Existing OCR user verification flags (returned as-is when no deletions are provided)
     * @returns Updated OCR user verification flags derived from remaining attachments
     */
    attachmentInfo_1.updateOCRUserVerifiedStateOnDelete = (attachmentInfo, deletedAttachments, currentFlags) => {
        if (!(deletedAttachments === null || deletedAttachments === void 0 ? void 0 : deletedAttachments.length)) {
            return currentFlags;
        }
        const deletedIds = new Set(deletedAttachments.map(item => item.id));
        const remainingAttachmentInfo = attachmentInfo
            .map((attachmentGroup) => attachmentGroup.filter((attachment) => !deletedIds.has(attachment.id)))
            .filter((attachmentGroup) => attachmentGroup.length > 0);
        return attachmentInfo_1.getOCRUserVerifiedStateFromAttachmentInfo(remainingAttachmentInfo);
    };
    /**
     * Categorizes attachments from the current iteration as ADDITIONAL_ARTIFACTS.
     * @param iteration The iteration object containing batchIds (used for validation/context)
     * @param attachmentBatchActivityInfo The full list of batch activity info (used for validation/context)
     * @param tempAttachmentInfo The current 2D array of attachment groups to filter current iteration attachments from
     * @returns Updated 2D array with foldersModelInfo replaced to ADDITIONAL_ARTIFACTS for all current iteration attachments
     */
    function addAdditionalArtifactsFolderInfo(iteration, attachmentBatchActivityInfo, tempAttachmentInfo) {
        const currentIterationAttachments = attachmentIteration.getCurrentIterationAttachments(iteration, attachmentBatchActivityInfo, tempAttachmentInfo);
        return currentIterationAttachments.map((group) => group.map((attachment) => (Object.assign(Object.assign({}, attachment), { foldersModelInfo: [
                {
                    folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS,
                    labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION
                }
            ] }))));
    }
    attachmentInfo_1.addAdditionalArtifactsFolderInfo = addAdditionalArtifactsFolderInfo;
    /**
     * Updates attachments with thumbnail details by matching.		 *
     * @param attachments 2D array of attachments to update
     * @param filesInfo Array of file info objects
     * @returns Updated 2D array of attachments with thumbnail fields set
     */
    attachmentInfo_1.updateAttachmentsWithThumbnails = (attachments, filesInfo) => {
        const parentIdToThumbnailMap = new Map();
        filesInfo.forEach((fileInfo) => {
            if (fileInfo.parentId) {
                parentIdToThumbnailMap.set(fileInfo.parentId, fileInfo);
            }
        });
        const docUIDToFileInfo = new Map();
        filesInfo.forEach((fileInfo) => {
            if (fileInfo.docUID) {
                docUIDToFileInfo.set(fileInfo.docUID, fileInfo);
            }
        });
        return attachments.map((group) => group.map((attachment) => {
            const fileInfo = docUIDToFileInfo.get(attachment.fileId);
            if (fileInfo && fileInfo.id) {
                const thumbnail = parentIdToThumbnailMap.get(fileInfo.id);
                if (thumbnail) {
                    return Object.assign(Object.assign({}, attachment), { thumbFileId: thumbnail.docUID || EMPTY_TEXT, thumbnailImageSize: thumbnail.fileSizeInBytes, thumbFileReferenceId: thumbnail.docId || 0 });
                }
            }
            return attachment;
        }));
    };
    /**
     * Checks if the given MIME type is one of the allowed image MIME types (JPEG, GIF, PNG).
     * @param mimeType The MIME type to check
     * @return True if the MIME type is an allowed image type, false otherwise
     */
    attachmentInfo_1.isImageMimeType = (mimeType) => {
        const allowedMimeTypes = [MimeType.IMAGE_JPEG, MimeType.IMAGE_GIF, MimeType.IMAGE_PNG];
        return allowedMimeTypes.includes(mimeType);
    };
})(attachmentInfo || (attachmentInfo = {}));
export var attachmentIteration;
(function (attachmentIteration) {
    /**
     * Creates an attachment iteration info object for intelligent photo capture processing.	 *
     * @param captureMode - The capture mode used for the photo capture session (e.g., estimator mode)
     * @param files - Array of File objects that were captured/selected for processing
     * @param iterationId - Unique identifier for this iteration instance
     * @param maxPhotoLimitForBatch - The maximum number of photos allowed per batch, used to calculate batch count
     * @returns A new IIntelligentPhotoCaptureAttachmentIterationInfo object with initial pending states
     */
    attachmentIteration.createAttachmentIterationInfo = (captureMode, files, iterationId, maxPhotoLimitForBatch) => {
        const validatedBatchLimit = maxPhotoLimitForBatch > 0 ? maxPhotoLimitForBatch : IntelligentAssignmentSummary.MAX_PHOTO_LIMIT_FOR_BATCH;
        return {
            batchCount: files.length % validatedBatchLimit === 0
                ? files.length / validatedBatchLimit
                : Math.floor(files.length / validatedBatchLimit) + 1,
            batchIds: [],
            cameraFiles: files.map((file) => ({
                imageName: file.name,
                cameraStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING
            })),
            captureMode: captureMode,
            damageProcessStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING,
            damageCallStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING,
            isRenderingCompleted: false,
            iterationFiles: files,
            iterationId: iterationId,
            iterationStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING,
            uploadingStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING
        };
    };
    /**
     * Generates a unique iteration identifier by comparing only against the existing attachment iteration info.
     * @param attachmentIterationInfo - Existing iteration info collection
     * @returns A unique numeric iteration identifier
     */
    attachmentIteration.generateUniqueIterationId = (attachmentIterationInfo) => {
        let uniqueIdentifier;
        do {
            uniqueIdentifier = new Date().getUTCMilliseconds();
        } while (attachmentIterationInfo.some((iterationInfo) => iterationInfo.iterationId === uniqueIdentifier));
        return uniqueIdentifier;
    };
    /**
     * Appends a file to the last iteration's files list and returns the updated iteration collection.
     * @param file - File to append to the last iteration
     * @param attachmentIterationInfo - Existing iteration collection
     * @param maxPhotoLimitForBatch - The maximum number of photos allowed per batch, used to recalculate batch count for the last iteration
     * @returns Updated iteration collection with file appended to the last item
     */
    attachmentIteration.addFileToLastIteration = (file, attachmentIterationInfo, maxPhotoLimitForBatch) => {
        if (!attachmentIterationInfo.length) {
            return attachmentIterationInfo;
        }
        const lastIterationIndex = attachmentIterationInfo.length - 1;
        return attachmentIterationInfo.map((iteration, index) => {
            var _a;
            return index === lastIterationIndex
                ? Object.assign(Object.assign({}, iteration), { batchCount: (iteration.iterationFiles.length + 1) % maxPhotoLimitForBatch === 0
                        ? (iteration.iterationFiles.length + 1) / maxPhotoLimitForBatch
                        : Math.floor((iteration.iterationFiles.length + 1) / maxPhotoLimitForBatch) + 1, cameraFiles: [
                        ...((_a = iteration.cameraFiles) !== null && _a !== void 0 ? _a : []),
                        {
                            imageName: file.name,
                            cameraStatus: IntelligentPhotoCaptureAttachmentIterationStatus.PENDING
                        }
                    ], iterationFiles: [...iteration.iterationFiles, file] }) : iteration;
        });
    };
    /**
     * Checks if all camera files in the given iteration have completed processing.
     * @param iteration - The attachment iteration info object to check
     * @returns True if all camera files have a status of COMPLETED, false otherwise
     */
    attachmentIteration.areAllCameraFilesCompleted = (iteration) => {
        var _a, _b;
        return (_b = (_a = iteration.cameraFiles) === null || _a === void 0 ? void 0 : _a.every((cameraFile) => cameraFile.cameraStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED)) !== null && _b !== void 0 ? _b : false;
    };
    /**
     * Returns the first iteration whose status is IN_PROGRESS.
     * @param attachmentIterationInfo Array of iteration info
     * @returns The first in-progress iteration, or undefined if none found
     */
    attachmentIteration.getInProgressIteration = (attachmentIterationInfo) => attachmentIterationInfo.find((iteration) => iteration.iterationStatus === IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS);
    /**
     * Returns the latest iteration from the attachmentIterationInfo array.
     * @param attachmentIterationInfo Array of iteration info
     * @returns The latest iteration or undefined if empty
     */
    attachmentIteration.getLatestIteration = (attachmentIterationInfo) => attachmentIterationInfo[attachmentIterationInfo.length - 1];
    /**
     * Determines the allowed file types for upload based on the capture mode. For UPLOAD_FILES mode, returns gallery-allowed file extensions excluding supported image types and explicitly excluded types.
     * @param captureMode The current capture mode to determine which file types to allow
     * @returns An array of allowed file extensions based on the capture mode
     */
    attachmentIteration.getAllowedFileTypes = (captureMode) => captureMode === IntelligentPhotoCaptureEstimatorCaptureMode.UPLOAD_FILES
        ? PhotoCaptureFileExtensionAllowedForGallery.filter(ext => !excludedUploadFileTypesSet.has(ext))
        : PhotoCaptureAllowedMimeTypeConstant;
    /**
     * Returns all attachments belonging to the current iteration by matching batchIds and imageUniqueIdentifier.
     * @param iteration The current iteration info
     * @param attachmentBatchActivityInfo The batch activity info array
     * @param attachmentInfo The 2D array of all attachments
     * @returns Array of attachments for the current iteration
     */
    attachmentIteration.getCurrentIterationAttachments = (iteration, attachmentBatchActivityInfo, attachmentInfo) => {
        // Collect all imageUniqueIdentifiers for this iteration's batches
        const idSet = new Set();
        for (const batchId of iteration.batchIds) {
            const batch = attachmentBatchActivityInfo.find(b => b.batchId === batchId);
            if (batch) {
                for (const attachment of batch.attachments) {
                    idSet.add(attachment.imageUniqueIdentifier);
                }
            }
        }
        // Filter all attachments by those ids, and wrap each in its own array
        return attachmentInfo
            .flat()
            .filter(att => idSet.has(att.id))
            .map(att => [att]);
    };
    /**
     * Generates a MIME type string for file input accept attribute based on the capture mode. For UPLOAD_FILES mode, it returns a string of allowed file extensions that are in the gallery but not in the allowed MIME types. For other modes, it returns a predefined string of accepted image MIME types.
     * @param captureMode The current capture mode to determine which MIME types to return
     * @returns A string of MIME types or file extensions for the accept attribute of a file input
     */
    attachmentIteration.getMimeTypesForCaptureMode = (captureMode) => captureMode === IntelligentPhotoCaptureEstimatorCaptureMode.UPLOAD_FILES
        ? `${'.' + attachmentIteration.getAllowedFileTypes(captureMode).join(',.')}`
        : PhotoCaptureConstants.ACCEPT_IMAGE_MIME;
    /**
     * Updates the damage call timestamp for the iteration matching the given iterationId.
     * @param attachmentIterationInfo Array of iteration info to update
     * @param iterationId The ID of the iteration to update the damage call timestamp for
     * @return Updated iteration info array when a match is found; otherwise the original input
     */
    attachmentIteration.updateIterationDamageCallAtByIterationId = (attachmentIterationInfo, iterationId) => {
        const targetIndex = attachmentIterationInfo.findIndex((iteration) => iteration.iterationId === iterationId);
        if (targetIndex === -1) {
            return attachmentIterationInfo;
        }
        return attachmentIterationInfo.map((iteration, index) => index === targetIndex ? Object.assign(Object.assign({}, iteration), { damageCallAt: Date.now() }) : iteration);
    };
    /**
     * Updates the damage call and process statuses for the iteration matching the given iterationId.
     * @param attachmentIterationInfo Array of iteration info to update
     * @param iterationId The ID of the iteration to update the damage statuses for
     * @param damageCallStatus Optional new status for damage call; if undefined, the existing value will be preserved
     * @param damageProcessStatus Optional new status for damage process; if undefined, the existing value will be preserved
     * @return Updated iteration info array when a match is found; otherwise the original input
     */
    attachmentIteration.updateIterationDamageStatusesByIterationId = (attachmentIterationInfo, iterationId, damageCallStatus, damageProcessStatus) => {
        const targetIndex = attachmentIterationInfo.findIndex((iteration) => iteration.iterationId === iterationId);
        if (targetIndex === -1) {
            return attachmentIterationInfo;
        }
        return attachmentIterationInfo.map((iteration, index) => index === targetIndex
            ? Object.assign(Object.assign(Object.assign({}, iteration), (damageCallStatus !== undefined && { damageCallStatus })), (damageProcessStatus !== undefined && { damageProcessStatus })) : iteration);
    };
})(attachmentIteration || (attachmentIteration = {}));
/**
 * Builds the damage detection POST request payload from iteration and batch state.
 
 * @param iterationId - ID of the completed iteration
 * @param contextId - Current context / reference ID from Redux state
 * @param instanceReferenceId - Optional orchestration instance reference ID used by damage APIs
 * @param attachmentIterationInfo - All known iterations from Redux state
 * @param attachmentBatchActivityInfo - All known batch activity records from Redux state
 * @param vehicle - Optional vehicle data from Redux state
 * @returns A fully assembled request payload, or null if the request should be skipped
 */
export const buildDamageDetectionRequest = (iterationId, contextId, attachmentIterationInfo, attachmentBatchActivityInfo, vehicle, instanceReferenceId, connectionId) => {
    var _a, _b;
    // Locate the completed iteration to retrieve its associated batchIds
    const targetIteration = attachmentIterationInfo.find(iterationInfo => iterationInfo.iterationId === iterationId);
    if (!targetIteration || !targetIteration.batchIds.length) {
        return null;
    }
    // Collect and deduplicate attachments from all batches belonging to this iteration
    const attachmentsByDocUid = {};
    attachmentBatchActivityInfo.forEach(batchInfo => {
        // Only process batches that belong to the current iteration
        if (!targetIteration.batchIds.includes(batchInfo.batchId)) {
            return;
        }
        batchInfo.attachments.forEach(attachment => {
            // Skip attachments without a valid docUID – they cannot be referenced by the damage API
            if (!attachment.docUID) {
                return;
            }
            // Keying by docUID automatically deduplicates attachments that appear in multiple batches
            attachmentsByDocUid[attachment.docUID] = attachment;
        });
    });
    const iterationAttachments = Object.values(attachmentsByDocUid);
    if (!iterationAttachments.length) {
        return null;
    }
    // Build vehicle info with available fields (flexible - doesn't require all fields to be present)
    const isValidModelYear = (vehicle === null || vehicle === void 0 ? void 0 : vehicle.modelYear) !== undefined && Number.isFinite(vehicle.modelYear) && vehicle.modelYear > 0;
    const viaVehicleInfo = vehicle
        ? {
            modelYear: isValidModelYear ? vehicle.modelYear : undefined,
            makeId: vehicle.makeId,
            makeDesc: (_a = vehicle.makeModel) === null || _a === void 0 ? void 0 : _a.trim(),
            modelId: vehicle.modelId,
            modelName: (_b = vehicle.modelDesc) === null || _b === void 0 ? void 0 : _b.trim(),
            mitchellVid: vehicle.mitchellVid,
            serviceBarcode: vehicle.serviceBarcode
        }
        : undefined;
    const trimmedConnectionId = connectionId === null || connectionId === void 0 ? void 0 : connectionId.trim();
    const requestId = trimmedConnectionId
        ? `${trimmedConnectionId}_${contextId}_${iterationId}`
        : `${contextId}_${iterationId}`;
    if ((viaVehicleInfo === null || viaVehicleInfo === void 0 ? void 0 : viaVehicleInfo.modelYear) && viaVehicleInfo.makeDesc && viaVehicleInfo.modelName) {
        return {
            referenceId: instanceReferenceId,
            requestId,
            attachments: iterationAttachments,
            viaVehicleInfo
        };
    }
    else {
        return {
            referenceId: instanceReferenceId,
            requestId,
            attachments: iterationAttachments
        };
    }
};
export const resolveDuplicateContentTypeImages = (attachments, vehicleInfo, iteration, attachmentBatchActivityInfo, vinOption) => {
    const { licensePlate, vinNo, odometer } = vehicleInfo || {};
    const categoryToFileTypesMap = getCategoryToFileTypesMapping();
    const flatAttachments = attachments.flatMap((group, groupIndex) => group.map((attachment, attachmentIndex) => ({
        attachment,
        groupIndex,
        attachmentIndex
    })));
    const getTextMatchParam = (contentType) => {
        if (contentType === IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE) {
            return licensePlate;
        }
        if (contentType === IntelligentPhotoCaptureEstimatorContentType.VIN ||
            contentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER) {
            return vinNo;
        }
        return odometer !== undefined ? String(odometer) : undefined;
    };
    const isVehicleInfoTextMatched = (contentType, detectedText) => {
        if (!hasValue(detectedText)) {
            return false;
        }
        if (contentType === IntelligentPhotoCaptureEstimatorContentType.ODOMETER) {
            return isOdometerWithinThreshold(detectedText, odometer);
        }
        const matchParam = getTextMatchParam(contentType);
        return matchParam !== undefined && detectedText === matchParam;
    };
    /**
     * Given a set of candidate attachments for a specific contentType, resolves which one wins.
     * Returns the `groupIndex-attachmentIndex` key of the winner.
     */
    const resolveWinner = (candidates, contentType, applyVehicleInfoPriority) => {
        let pool = [...candidates];
        if (applyVehicleInfoPriority) {
            // Priority 1: isImageEdited
            const userEditedPool = pool.filter((candidate) => candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.contentType === contentType && folderModel.isImageEdited));
            if (userEditedPool.length) {
                pool = userEditedPool;
            }
            // Priority 2: text match against the corresponding function parameter
            const matchParam = getTextMatchParam(contentType);
            if (matchParam !== undefined) {
                const textMatchPool = pool.filter((candidate) => candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.contentType === contentType &&
                    isVehicleInfoTextMatched(contentType, folderModel.text)));
                if (textMatchPool.length) {
                    pool = textMatchPool;
                }
            }
        }
        // Priority 3 (final): highest confidence
        let winnerKey = `${pool[0].groupIndex}-${pool[0].attachmentIndex}`;
        let winnerConfidence = Number.NEGATIVE_INFINITY;
        pool.forEach((candidate) => {
            const scores = candidate.attachment
                .foldersModelInfo.filter((folderModel) => folderModel.contentType === contentType)
                .map((folderModel) => { var _a; return (_a = folderModel.confidence) !== null && _a !== void 0 ? _a : 0; });
            const candidateConfidence = scores.reduce((maxScore, score) => Math.max(maxScore, score), Number.NEGATIVE_INFINITY);
            if (candidateConfidence > winnerConfidence) {
                winnerKey = `${candidate.groupIndex}-${candidate.attachmentIndex}`;
                winnerConfidence = candidateConfidence;
            }
        });
        return winnerKey;
    };
    // Map of "groupIndex-attachmentIndex" => set of contentTypes whose folder entry should be demoted
    const demoteMap = new Map();
    const categoriesToProcess = [
        { folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION, isVehicleInfo: true },
        { folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS, isVehicleInfo: false }
    ];
    categoriesToProcess.forEach(({ folderTypeCode, isVehicleInfo }) => {
        const contentTypes = categoryToFileTypesMap[folderTypeCode];
        contentTypes.forEach((contentType) => {
            const candidates = flatAttachments.filter((flat) => {
                var _a;
                return (_a = flat.attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.some((folderModel) => folderModel.folderTypeCode === folderTypeCode && folderModel.contentType === contentType);
            });
            if (candidates.length <= 1)
                return;
            const winnerKey = resolveWinner(candidates, contentType, isVehicleInfo);
            candidates.forEach((candidate) => {
                const candidateKey = `${candidate.groupIndex}-${candidate.attachmentIndex}`;
                if (candidateKey !== winnerKey) {
                    if (!demoteMap.has(candidateKey)) {
                        demoteMap.set(candidateKey, new Set());
                    }
                    demoteMap.get(candidateKey).add(contentType);
                }
            });
        });
    });
    applyVinLikeCrossTypeDemotion({
        flatAttachments,
        demoteMap,
        vinOption,
        vinNo,
        isVehicleInfoTextMatched: (contentType, detectedText) => isVehicleInfoTextMatched(contentType, detectedText)
    });
    // Build deduplicatedAttachmentGroups using the demoteMap logic (same as before)
    const deduplicatedAttachmentGroups = attachments.map((group, groupIndex) => group.map((attachment, attachmentIndex) => {
        const attachmentKey = `${groupIndex}-${attachmentIndex}`;
        const contentTypesToDemote = demoteMap.get(attachmentKey);
        if (!contentTypesToDemote) {
            return attachment;
        }
        const updatedAttachment = attachment.foldersModelInfo.reduce((currentAttachment, folderModel) => {
            if (!folderModel.contentType || !contentTypesToDemote.has(folderModel.contentType)) {
                return currentAttachment;
            }
            const currentFolders = currentAttachment.foldersModelInfo || [];
            const currentIndex = currentFolders.indexOf(folderModel);
            if (currentIndex === -1) {
                return currentAttachment;
            }
            if (currentFolders.length > 1) {
                const foldersModelInfo = currentFolders.filter((_, index) => index !== currentIndex);
                return Object.assign(Object.assign({}, currentAttachment), { foldersModelInfo });
            }
            const foldersModelInfo = [
                Object.assign(Object.assign({}, folderModel), { folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS, text: undefined })
            ];
            return Object.assign(Object.assign({}, currentAttachment), { foldersModelInfo });
        }, attachment);
        return updatedAttachment;
    }));
    if (iteration && attachmentBatchActivityInfo && attachmentBatchActivityInfo.length) {
        // Find only the updated attachments (compare with input)
        const updatedAttachments = attachments.flatMap((group, groupIdx) => group
            .map((attachment, attIdx) => {
            var _a;
            const updated = (_a = deduplicatedAttachmentGroups[groupIdx]) === null || _a === void 0 ? void 0 : _a[attIdx];
            return updated && JSON.stringify(updated) !== JSON.stringify(attachment) ? [updated] : null;
        })
            .filter(Boolean));
        // Remove any attachments from currentIterationAttachments whose id is already present in updatedAttachments
        const updatedIds = new Set(updatedAttachments.flat().map(att => att.id));
        const currentIterationAttachments = attachmentIteration
            .getCurrentIterationAttachments(iteration, attachmentBatchActivityInfo, attachments)
            .filter(group => group.length > 0 && !updatedIds.has(group[0].id));
        return [...updatedAttachments, ...currentIterationAttachments];
    }
    else {
        return deduplicatedAttachmentGroups;
    }
};
/**
 * Provides a mapping from artifact category names to their corresponding label names for display purposes.
 * The mapping is static and defined once at module load time.
 */
const getCategoryToLabelNameMapping = {
    [IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS]: IntelligentPhotoCaptureEstimatorCategoryLabelName.ADDITIONAL_ARTIFACTS,
    [IntelligentPhotoCaptureArtifactsCategoryNames.COLLECTING_ARTIFACTS]: IntelligentPhotoCaptureEstimatorCategoryLabelName.COLLECTING_ARTIFACTS,
    [IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS]: IntelligentPhotoCaptureEstimatorCategoryLabelName.FOUR_CORNERS,
    [IntelligentPhotoCaptureArtifactsCategoryNames.PRIOR_DAMAGE]: IntelligentPhotoCaptureEstimatorCategoryLabelName.PRIOR_DAMAGE,
    [IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_DAMAGE]: IntelligentPhotoCaptureEstimatorCategoryLabelName.VEHICLE_DAMAGE,
    [IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION]: IntelligentPhotoCaptureEstimatorCategoryLabelName.VEHICLE_INFORMATION
};
/**
 * Gets the display label for an album header based on the folder type code. If the folder type code does not match any known category, it defaults to "Collecting Artifacts".
 * @param folderTypeCode The folder type code associated with the album, used to determine the appropriate display label
 * @return The display label for the album header corresponding to the given folder type code, or "Collecting Artifacts" if no match is found
 */
export const getAlbumHeader = (folderTypeCode) => {
    const labelName = getCategoryToLabelNameMapping[folderTypeCode] ||
        IntelligentPhotoCaptureEstimatorCategoryLabelName.COLLECTING_ARTIFACTS;
    return translateFn(labelName);
};
/**
 * Builds the estimator summary header text from vehicle details.
 * Falls back to the default header text when no vehicle fields are available.
 * @param vehicle - partial vehicle object with optional string fields
 * @returns assembled header text string
 */
export const getEstimatorSummaryHeaderText = (vehicle) => [vehicle === null || vehicle === void 0 ? void 0 : vehicle.modelColor, vehicle === null || vehicle === void 0 ? void 0 : vehicle.modelYear, vehicle === null || vehicle === void 0 ? void 0 : vehicle.makeModel, vehicle === null || vehicle === void 0 ? void 0 : vehicle.modelDesc].filter(Boolean).join(' ') ||
    IntelligentAssignmentSummary.ASSIGNMENT_SUMMARY_DEFAULT_HEADER_TEXT;
/**
 * Maps content types to their corresponding label names.
 * @param countryCode Country code for localization (affects odometer/mileage label)
 * @returns Mapping of content type to label name
 */
export const getContentTypeToLabelMapping = (countryCode) => ({
    [IntelligentPhotoCaptureEstimatorContentType.VIN]: PhotoCaptureDownloadArtifactsLabelNames.VIN,
    [IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER]: PhotoCaptureDownloadArtifactsLabelNames.VIN_STICKER,
    [IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE]: PhotoCaptureDownloadArtifactsLabelNames.PLATE,
    [IntelligentPhotoCaptureEstimatorContentType.ODOMETER]: countryCode === CountryCode.UNITED_STATES
        ? PhotoCaptureDownloadArtifactsLabelNames.ODOMETER
        : PhotoCaptureDownloadArtifactsLabelNames.MILEAGE,
    [IntelligentPhotoCaptureEstimatorContentType.FRONTLEFT]: PhotoCaptureDownloadArtifactsLabelNames.FRONT_LEFT,
    [IntelligentPhotoCaptureEstimatorContentType.FRONTRIGHT]: PhotoCaptureDownloadArtifactsLabelNames.FRONT_RIGHT,
    [IntelligentPhotoCaptureEstimatorContentType.REARLEFT]: PhotoCaptureDownloadArtifactsLabelNames.REAR_LEFT,
    [IntelligentPhotoCaptureEstimatorContentType.REARRIGHT]: PhotoCaptureDownloadArtifactsLabelNames.REAR_RIGHT
});
/**
 * Builds a tile order mapping for a specific category using translated labels.
 * This ensures sorting works correctly across all locales by comparing translated text to translated text.
 * Note: Not cached because the mapping depends on the current i18n language (translateFn).
 * If cached without including the language in the key, language changes would return stale translations.
 * Since the mapping is small (max 4 entries per category), the allocation cost is negligible.
 * @param folderTypeCode The folder type code/category
 * @param countryCode Country code for localization
 * @returns A mapping of lowercase translated label names to their sort order, or undefined if category doesn't support ordering
 */
const getTileOrderMappingByCategory = (folderTypeCode, countryCode) => {
    const categoryToFileTypesMapping = getCategoryToFileTypesMapping();
    const contentTypes = categoryToFileTypesMapping[folderTypeCode];
    if (!contentTypes || contentTypes.length === 0) {
        return undefined;
    }
    const contentTypeToLabelMap = getContentTypeToLabelMapping(countryCode);
    const orderMapping = {};
    contentTypes.forEach((contentType, index) => {
        const labelName = contentTypeToLabelMap[contentType];
        if (labelName) {
            const translatedLabel = translateFn(labelName);
            orderMapping[translatedLabel.toLowerCase()] = index;
        }
    });
    return orderMapping;
};
export const getTileOrderByCategory = (folderTypeCode, tile, countryCode = CountryCode.UNITED_STATES) => {
    var _a, _b, _c;
    if (!folderTypeCode) {
        return Number.MAX_SAFE_INTEGER;
    }
    const primaryTagLabel = (((_b = (_a = tile.tags) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.text) || '').toLowerCase();
    const orderMapping = getTileOrderMappingByCategory(folderTypeCode, countryCode);
    if (!orderMapping) {
        return Number.MAX_SAFE_INTEGER;
    }
    return (_c = orderMapping[primaryTagLabel]) !== null && _c !== void 0 ? _c : Number.MAX_SAFE_INTEGER;
};
export const sortAlbumTilesForDisplay = (albumsToSort, countryCode = CountryCode.UNITED_STATES) => {
    return albumsToSort.map((album) => {
        const shouldSortCurrentAlbum = album.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION ||
            album.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS;
        const sortedTiles = shouldSortCurrentAlbum
            ? [...album.tiles]
                .map((tile, index) => ({ tile, index }))
                .sort((a, b) => {
                const orderA = getTileOrderByCategory(album.folderTypeCode, a.tile, countryCode);
                const orderB = getTileOrderByCategory(album.folderTypeCode, b.tile, countryCode);
                if (orderA === orderB) {
                    return a.index - b.index;
                }
                return orderA - orderB;
            })
                .map(({ tile }) => tile)
            : album.tiles;
        return Object.assign(Object.assign({}, album), { tiles: sortedTiles });
    });
};
/**
 * Retrieves the PRTC description text for a given part code from the PRTC details data.
 * @param partCode The part code to look up in the PRTC details data
 * @param prtcDetailsData The PRTC details data containing descriptions for various part codes
 * @returns The localized description text for the given part code, or an empty string if not found
 */
export const getPrtcDescriptionFromData = (partCode, prtcDetailsData) => {
    var _a;
    if (!(partCode === null || partCode === void 0 ? void 0 : partCode.trim())) {
        return EMPTY_TEXT;
    }
    const trimmedCode = partCode.trim();
    if (!((_a = prtcDetailsData === null || prtcDetailsData === void 0 ? void 0 : prtcDetailsData.prtcs) === null || _a === void 0 ? void 0 : _a.length)) {
        return EMPTY_TEXT;
    }
    const matchedPrtc = prtcDetailsData.prtcs.find(item => item.prtc === trimmedCode);
    if (!(matchedPrtc === null || matchedPrtc === void 0 ? void 0 : matchedPrtc.descriptions)) {
        return EMPTY_TEXT;
    }
    return TranslationService.getCurrentLanguage()
        .toLowerCase()
        .startsWith(LocaleCodes.FR.toLowerCase())
        ? matchedPrtc.descriptions.fr
        : matchedPrtc.descriptions.en;
};
/**
 * Returns access token for authentication calls only when session has a token
 * and local storage does not have one yet.
 */
export const getAccessTokenForAuthentication = () => {
    const sessionAccessToken = typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(GPCLoaderConstants.ACCESS_TOKEN) || undefined
        : undefined;
    const localAccessToken = typeof localStorage !== 'undefined'
        ? localStorage.getItem(GPCLoaderConstants.ACCESS_TOKEN) || undefined
        : undefined;
    return sessionAccessToken && !localAccessToken ? sessionAccessToken : undefined;
};
/**
 * Calculates the delay in milliseconds before the next token refresh should occur.
 * Returns null when the token is missing or unparseable (caller should retry after TOKEN_PARSE_RETRY_DELAY_MS).
 * Returns TOKEN_PARSE_RETRY_DELAY_MS when the token lifetime is shorter than the lead-time buffer
 * to prevent hammering the auth service.
 */
export const getRefreshDelayMs = (authToken) => {
    if (!authToken) {
        return null;
    }
    try {
        const parsedAuthToken = JSON.parse(authToken);
        const expiresInSeconds = Number(parsedAuthToken && parsedAuthToken.expires_in);
        if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
            return null;
        }
        const expiresInMs = expiresInSeconds * 1000;
        if (expiresInMs <= GPCLoaderConstants.AUTH_TOKEN_REFRESH_LEAD_TIME_MS) {
            return GPCLoaderConstants.TOKEN_PARSE_RETRY_DELAY_MS;
        }
        return expiresInMs - GPCLoaderConstants.AUTH_TOKEN_REFRESH_LEAD_TIME_MS;
    }
    catch (_a) {
        return null;
    }
};
/**
 * Parses the auth token JSON string and stores the access_token in localStorage.
 * Silently ignores missing, empty, or malformed tokens to keep the refresh loop resilient.
 */
export const updateAccessToken = (authToken) => {
    if (!authToken) {
        return;
    }
    try {
        const parsedAuthToken = JSON.parse(authToken);
        if (parsedAuthToken && parsedAuthToken.access_token) {
            localStorage.setItem(GPCLoaderConstants.ACCESS_TOKEN, parsedAuthToken.access_token);
            sessionStorage.setItem(GPCLoaderConstants.ACCESS_TOKEN, parsedAuthToken.access_token);
        }
    }
    catch (_a) {
        return;
    }
};
/**
 * Determines whether a GET damage detection API response carries meaningful data.
 * A response is considered valid when at least one of the key fields (requestId,
 * analysisType, recommendations, error) is present and non-empty.
 * Extracted from the saga to allow isolated unit testing without saga overhead.
 * @param response - The raw response object from the damage detection GET API
 * @returns true when the response contains usable data, false otherwise
 */
export const isValidDamageDetectionResponse = (response) => {
    var _a, _b;
    return !!response &&
        (!!response.requestId || !!response.analysisType || !!response.error || ((_b = (_a = response.recommendations) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0);
};
/**
 * Applies damage detection results to the existing tempAttachmentInfo by annotating
 * relevant attachments with the appropriate damage folder category.
 * @param tempAttachmentInfo - Current 2D array of attachment groups from Redux state
 * @param damageDetectionResponse - Successful GET damage detection API response
 * @returns Updated 2D array with damage category folder info applied to relevant attachments
 */
export const getUpdatedAttachmentInfoFromDamageDetection = (tempAttachmentInfo, damageDetectionResponse) => {
    if (!damageDetectionResponse ||
        !damageDetectionResponse.recommendations ||
        !damageDetectionResponse.recommendations.length) {
        return tempAttachmentInfo;
    }
    const recommendationByUidMap = new Map();
    damageDetectionResponse.recommendations.forEach((recommendation) => {
        if (!recommendation || !recommendation.relevantImages || !recommendation.relevantImages.length) {
            return;
        }
        recommendation.relevantImages.forEach((uid) => {
            if (!uid) {
                return;
            }
            if (!recommendationByUidMap.has(uid)) {
                recommendationByUidMap.set(uid, []);
            }
            recommendationByUidMap.get(uid).push(recommendation);
        });
    });
    if (!recommendationByUidMap.size) {
        return tempAttachmentInfo;
    }
    return tempAttachmentInfo.map((attachmentGroup) => attachmentGroup.map((attachment) => {
        const matchedRecommendations = recommendationByUidMap.get(attachment.fileId);
        if (!(matchedRecommendations === null || matchedRecommendations === void 0 ? void 0 : matchedRecommendations.length)) {
            return attachment;
        }
        const currentFolders = attachment.foldersModelInfo || [];
        const preservedFolders = currentFolders.filter((folderModel) => folderModel.folderTypeCode !== IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS);
        const damageFolders = matchedRecommendations.map((recommendation) => ({
            folderTypeCode: IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_DAMAGE,
            confidence: recommendation.confidence,
            labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.PRTC,
            text: recommendation.partCode
        }));
        return Object.assign(Object.assign({}, attachment), { foldersModelInfo: [...preservedFolders, ...damageFolders] });
    }));
};
/**
 * Filters updated attachment groups and returns only those attachments that are
 * explicitly referenced by damage detection recommendations.
 * @param updatedAttachmentInfoWithDamageDetection - Attachment groups after damage folder enrichment
 * @param damageDetectionResponse - Successful GET damage detection API response
 * @returns Filtered attachment groups containing only recommendation-linked attachments
 */
export const getAttachmentInfoFromDamageDetection = (updatedAttachmentInfoWithDamageDetection, damageDetectionResponse) => {
    var _a;
    const normalizedAttachmentInfo = updatedAttachmentInfoWithDamageDetection || [];
    if (!((_a = damageDetectionResponse === null || damageDetectionResponse === void 0 ? void 0 : damageDetectionResponse.recommendations) === null || _a === void 0 ? void 0 : _a.length) || !normalizedAttachmentInfo.length) {
        return normalizedAttachmentInfo;
    }
    const relevantImageIds = new Set(damageDetectionResponse.recommendations.flatMap((recommendation) => recommendation.relevantImages || []));
    if (!relevantImageIds.size) {
        return normalizedAttachmentInfo;
    }
    return normalizedAttachmentInfo
        .map((attachmentGroup) => attachmentGroup.filter((attachment) => relevantImageIds.has(attachment.fileId)))
        .filter((attachmentGroup) => attachmentGroup.length > 0);
};
export const getUpstreamVinStatus = (upstreamVin, countryCode, photoCaptureVehicle) => {
    if (!(upstreamVin === null || upstreamVin === void 0 ? void 0 : upstreamVin.trim())) {
        return undefined;
    }
    if (!photoCaptureVehicle) {
        return IntelligentPhotoCaptureVinDecodeStatus.PARTIAL;
    }
    const hasVehicleValue = (value) => {
        if (typeof value === 'number') {
            return Number.isFinite(value) && value > 0;
        }
        if (typeof value === 'string') {
            const numValue = Number(value.trim());
            return Number.isFinite(numValue) && numValue > 0;
        }
        return false;
    };
    const isUnitedState = countryCode === CountryCode.UNITED_STATES;
    if (isUnitedState) {
        const hasUnitedStateCountryVehicleIds = hasVehicleValue(photoCaptureVehicle.mitchellVid) &&
            hasVehicleValue(photoCaptureVehicle.pinVid) &&
            hasVehicleValue(photoCaptureVehicle.nadaVid);
        return hasUnitedStateCountryVehicleIds
            ? IntelligentPhotoCaptureVinDecodeStatus.FULL
            : IntelligentPhotoCaptureVinDecodeStatus.PARTIAL;
    }
    const hasCanadaVehicleIds = hasVehicleValue(photoCaptureVehicle.mitchellVid) &&
        hasVehicleValue(photoCaptureVehicle.pinVid) &&
        hasVehicleValue(photoCaptureVehicle.redbookVid);
    return hasCanadaVehicleIds
        ? IntelligentPhotoCaptureVinDecodeStatus.FULL
        : IntelligentPhotoCaptureVinDecodeStatus.PARTIAL;
};
const attachmentFolderTypeSortOrder = [
    IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION,
    IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS,
    IntelligentPhotoCaptureArtifactsCategoryNames.PRIOR_DAMAGE,
    IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_DAMAGE,
    IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS
];
const isNestedAttachmentArray = (attachmentInfo) => Array.isArray(attachmentInfo[0]);
const sortAlbumsByFolderTypeCode = (albums) => {
    const getFolderTypeSortIndex = (folderTypeCode) => {
        const folderTypeIndex = attachmentFolderTypeSortOrder.indexOf(folderTypeCode);
        return folderTypeIndex === -1 ? attachmentFolderTypeSortOrder.length : folderTypeIndex;
    };
    return [...albums].sort((album1, album2) => {
        const album1SortOrder = getFolderTypeSortIndex(album1.folderTypeCode);
        const album2SortOrder = getFolderTypeSortIndex(album2.folderTypeCode);
        return album1SortOrder !== album2SortOrder
            ? album1SortOrder - album2SortOrder
            : Number(album1.id) - Number(album2.id);
    });
};
const flattenAlbumTiles = (albums) => albums.flatMap((album) => {
    const subAlbums = album.subAlbums || [];
    const flattenedSubAlbumTiles = subAlbums.length > 0 ? flattenAlbumTiles(sortAlbumsByFolderTypeCode(subAlbums)) : [];
    return [...album.tiles, ...flattenedSubAlbumTiles];
});
/**
 * Produces a sorted and deduplicated list of gallery entries for the estimator thumbnail gallery.
 *
 * This function organizes attachments into categorized albums, orders them according to
 * folder type and country/vehicle-specific rules, and returns a flattened list of entries
 * ready for carousel display. Each entry maps a tile ID to its corresponding attachment.
 *
 * Processing steps:
 * 1. Flattens the attachment info if provided as nested array (2D -> 1D)
 * 2. Returns empty array if no attachments are provided
 * 3. Organizes attachments into categorized albums (VEHICLE_INFORMATION, FOUR_CORNERS, etc.)
 * 4. Flattens and sorts albums by folder type and tile order
 * 5. Maps sorted tiles back to their original attachments with tile IDs
 * 6. Filters out entries with missing attachments
 * 7. Falls back to index-based ordering if no categorized tiles are available
 *
 * @param {IPhotoCaptureAttachmentFileInfo[] | IPhotoCaptureAttachmentFileInfo[][]} attachmentInfo -
 *        Photo capture attachment information, either as a flat 1D array or nested 2D array of attachment groups.
 *        Each attachment contains file info, metadata, and folder categorization details.
 * @param {string} [countryCode='US'] -
 *        Country code for localization and country-specific tile ordering (e.g., 'US', 'CA').
 *        Used to determine tile sort order within categories and translated labels.
 * @param {SAGASERVICEONLY_IIntelligentPhotoCaptureVehicle | null} [photoCaptureVehicle] -
 *        Optional vehicle information used for intelligent organization and categorization of images
 *        based on vehicle-specific requirements and validation rules.
 * @param {boolean} [isConfidence] -
 *        Optional flag indicating whether confidence scoring should be considered during
 *        album organization and image categorization.
 * @param {IIntelligentPhotoCapturePrtcDetailsResponse} [prtcDetailsData] -
 *        Optional PRTC details data used to enrich attachment metadata and influence categorization
 *        based on part code descriptions and confidence scores.
 * @param {SAGASERVICEONLY_IIntelligentPhotoCaptureVehicle} [userVehicleInfo] -
 *        Optional user vehicle information that can be used to further refine attachment organization
 *        and categorization based on user-specific vehicle details and history.
 * @returns {Array<{ tileId: number; attachment: IPhotoCaptureAttachmentFileInfo }>}
 *         Array of gallery entries, each containing a tile ID and its corresponding attachment.
 *         Entries are ordered according to folder type priority and country-specific rules.
 *         Returns empty array if no attachments are provided or all attachments lack fileId.
 */
export const getOrderedEstimatorGalleryEntries = (attachmentInfo, countryCode = CountryCode.UNITED_STATES, photoCaptureVehicle, isConfidence, prtcDetailsData, userVehicleInfo) => {
    const flatAttachmentInfo = isNestedAttachmentArray(attachmentInfo)
        ? attachmentInfo.flat()
        : [...attachmentInfo];
    if (!flatAttachmentInfo.length) {
        return [];
    }
    const organizedAlbums = albumOrganizer.organizeImagesIntoAlbums(flatAttachmentInfo, countryCode, IntelligentAssignmentSummary.SUMMARY, false, photoCaptureVehicle, isConfidence, prtcDetailsData, userVehicleInfo).albums;
    const orderedTiles = flattenAlbumTiles(sortAlbumsByFolderTypeCode(sortAlbumTilesForDisplay(organizedAlbums, countryCode)));
    if (!orderedTiles.length) {
        return flatAttachmentInfo.map((attachment, index) => ({
            tileId: index,
            attachment
        }));
    }
    const attachmentById = new Map();
    for (const attachment of flatAttachmentInfo) {
        attachmentById.set(attachment.id, attachment);
    }
    return orderedTiles
        .map((tile) => ({
        tileId: tile.id,
        attachment: attachmentById.get(tile.id)
    }))
        .filter((entry) => !!entry.attachment);
};
/**
 * Determines whether file upload status is FAILED.
 * @param fileInfo - The file information object containing upload status and other metadata
 * @returns true when upload status is FAILED, otherwise false
 */
export const isFileUploadStatusFailed = (fileInfo) => fileInfo.uploadStatus === FileUploadStatus.FAILED;
export var vinDecode;
(function (vinDecode) {
    /**
     * Maps vehicle info domain data into estimatorVehicleInfo payload shape for attachment updates.
     * @param vehicleInfoData - The vehicle information data obtained from VIN decode or manual selection.
     * @param vehicleData - Optional existing vehicle data to fall back on for missing fields, such as from Redux state. This allows the function to populate estimatorVehicleInfo even when VIN decode results are incomplete, by leveraging any previously known vehicle details.
     * @returns An object containing mapped vehicle information fields in the shape expected by the estimatorVehicleInfo payload, or undefined if mapping cannot be performed. The function prioritizes VIN decode results but falls back to existing vehicle data when necessary to maximize the completeness of the returned vehicle information.
     */
    vinDecode.mapVehicleInfoData = (vehicleInfoData, vehicleData) => {
        var _a, _b, _c, _d, _e, _f;
        const processedVehicle = (_a = processVinData(vehicleInfoData)) === null || _a === void 0 ? void 0 : _a.Value;
        return {
            makeModel: ((_b = processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.Make) === null || _b === void 0 ? void 0 : _b.Value) || (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.make),
            makeId: ((_c = processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.Make) === null || _c === void 0 ? void 0 : _c.Id) || (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.makeId),
            modelDesc: ((_d = processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.Model) === null || _d === void 0 ? void 0 : _d.Value) || (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.model),
            modelId: ((_e = processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.Model) === null || _e === void 0 ? void 0 : _e.Id) || (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.modelId),
            modelYear: ((_f = processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.Year) === null || _f === void 0 ? void 0 : _f.Value) || (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.serviceYear),
            mitchellVid: (processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.Id) || (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.vid),
            pinVid: vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.pinVid,
            nadaVid: vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.nadaVid,
            redbookVid: vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.redbookControlNumber,
            serviceBarcode: Number(processedVehicle === null || processedVehicle === void 0 ? void 0 : processedVehicle.ServiceBarcode) || Number(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.serviceBarcode) || undefined
        };
    };
})(vinDecode || (vinDecode = {}));
/**
 * Returns the default translated toast message key for a given assignment summary operation.
 * @param operationType The assignment summary operation type used to determine the success message.
 * @returns The matching success toast message key, or EMPTY_TEXT when no default applies.
 */
export const getDefaultToastMessage = (operationType) => {
    switch (operationType) {
        case IntelligentPhotoCaptureAssignmentSummaryStatus.SUBMIT:
            return IntelligentAssignmentSummary.SENT_SUCCESSFULLY;
        case IntelligentPhotoCaptureAssignmentSummaryStatus.COPY:
        case IntelligentPhotoCaptureAssignmentSummaryStatus.MOVE:
            return IntelligentAssignmentSummary.PHOTO_MOVED_SUCCESSFULLY;
        case IntelligentPhotoCaptureAssignmentSummaryStatus.DELETE:
            return IntelligentAssignmentSummary.PHOTO_DELETED_SUCCESSFULLY;
        case IntelligentPhotoCaptureAssignmentSummaryStatus.REPLACE:
        case IntelligentPhotoCaptureAssignmentSummaryStatus.RETAKE:
            return IntelligentAssignmentSummary.PHOTO_REPLACED_SUCCESSFULLY;
        default:
            return EMPTY_TEXT;
    }
};
export const getPhotoCaptureDeviceMetaInfo = () => {
    const browserInfo = sessionStorage.getItem(IntelligentAssignmentSummary.BROWSER_INFO_STORAGE_KEY) || EMPTY_TEXT;
    const deviceType = sessionStorage.getItem(IntelligentAssignmentSummary.DEVICE_TYPE_STORAGE_KEY) || EMPTY_TEXT;
    const osInfo = sessionStorage.getItem(IntelligentAssignmentSummary.OS_INFO_STORAGE_KEY) || EMPTY_TEXT;
    return Object.assign(Object.assign(Object.assign({}, (browserInfo && { browserInfo })), (deviceType && { deviceType })), (osInfo && { osInfo }));
};
export const rotationFromDevice = (beta, gamma, currentScreenAngle) => {
    if (currentScreenAngle !== IntelligentPhotoCaptureCameraConstants.SCREEN_ANGLE_ZERO ||
        beta === null ||
        gamma === null) {
        return IntelligentPhotoCaptureRotationAngle.DEGREE_0;
    }
    if (Math.abs(gamma) > IntelligentPhotoCaptureCameraConstants.ORIENTATION_THRESHOLD) {
        return gamma > IntelligentPhotoCaptureCameraConstants.SCREEN_ANGLE_ZERO
            ? IntelligentPhotoCaptureRotationAngle.DEGREE_90
            : IntelligentPhotoCaptureRotationAngle.DEGREE_270;
    }
    if (beta < -IntelligentPhotoCaptureCameraConstants.ORIENTATION_THRESHOLD) {
        return IntelligentPhotoCaptureRotationAngle.DEGREE_180;
    }
    return IntelligentPhotoCaptureRotationAngle.DEGREE_0;
};
//# sourceMappingURL=intelligentPhotoCaptureEstimatorUtilities.js.map